// gamesession.ts ↔ the live SINGLE-PLAYER session driver: Game::StartSim's
// display partition + form construction (game/game.cpp:1276-1392, 2030-2105),
// Game::RunSimulation's game loop (game.cpp:1801-1844), Game::FilterEvent
// (game.cpp:2271-2333) and the SP slice of SimUIForm::OnTimer
// (game/SimUI.cpp:768-871) — a loaded Sim + the real forms.ini HUD over one
// shared back dib, paced by a SCRIPTED host clock (1/100s ticks).
//
// The C++ loop blocks inside HostGetEvent; a browser (and this harness) can't,
// so the embedder advances the clock with advanceTicks(n) and each tick pumps
// GetEvent(ctWait=0) dry, dispatching every returned event exactly the way
// game.cpp's loop does (FilterEvent first, gevm.DispatchEvent after). The SAME
// pump is Form::DoModal's nested modal loop (form.cpp:578-622) — see
// advanceTicks/DrainModalExits — and it hosts the in-game build/command UI
// (BuildUI, ui/buildui.ts: UnitMenu + the build/placement forms).
//
// KNOWN SEMANTICS preserved exactly (proven by this repo's oracle corpus):
//  1. SP passes ctx.cmdq DIRECTLY to Simulation.Update (SimUI.cpp:856
//     `gsim.Update(&gcmdq)`): the sim snapshots the count, routes, and clears —
//     including the enqueued-during-routing wipe quirk. NOTHING else may clear
//     or flush the queue (the flush-to-server arm is MP-only).
//  2. The trigger pump (ggame.UpdateTriggers) is serviced by FilterEvent on
//     every event-loop pass — two session policies:
//       'serviced' (default): RunPendingTriggerUpdate at the top of each
//         dispatch pass (game.cpp:2278) — the real client's behavior EVEN
//         HEADLESS: Game::ScheduleUpdateTriggers POSTS updateTriggersEvent
//         (game.cpp:2244-2255) and EventMgr::GetEvent returns posted events
//         before HostGetEvent (event.cpp), so FilterEvent always gets a pass.
//         PROVEN by auto/live-frame.mjs: 'serviced' state-gates bit-identical
//         vs the headless WASM client across live windows; 'starved' diverges
//         in TriggerMgr::m_tLastUpdate within 6 updates (kcupdTriggerMgrUpdate).
//       'starved': never run it — a DIAGNOSTIC mode (no real-client
//         counterpart; the one genuinely-killed pump — the post-load one,
//         game.cpp:1800 — is modeled by the ctor under BOTH policies). See
//         sim/src/sim/driver.ts fSuppressTriggerPump for the same seam.
//  3. Paint pacing: gamePaintEvent comes out of the EventMgr's CheckPaintFPS;
//     DrawFrame runs on paint events only (game.cpp:2285-2295).
//
// The sim must be LOADED by the embedder (Sim.load with multiplayer:false and
// the fog RevealFog host seam) — this object owns the client-side host seams
// only (event posting, redraw flags, the alert label, the minimap radar).

import type { Sim } from '../../../sim/src/index.ts';
import { IniReader } from '../../../sim/src/data/ini.ts';
import type { IniSection } from '../../../sim/src/data/ini.ts';
import type { Stream } from '../../../sim/src/sm/stream.ts';
import { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import type { UpdateMap, TRectSmall } from '../gfx/updatemap.ts';
import type { CoordMap } from '../gfx/coords.ts';
import { kpcMax } from '../gfx/coords.ts';
import type { TexAtlasMgr } from '../gfx/texatlasmgr.ts';
import type { PackFileReader } from '../io/packfile.ts';
import type { TileMap } from '../gfx/tilemap.ts';
// ?dom HUD: hudFont() hands the DOM the kifntHud bitmap font (off the renderer's
// gapfnt array) so the DOM readouts are pixel-faithful to the canvas controls.
import type { Font } from '../gfx/font.ts';
import {
  StringTable,
  kidsMinerUnderAttack,
  kidsLowPower,
  kidsBaseUnderAttack,
  kidsWarehouseTooFull,
  kidsExitHost,
  kidsExitClient,
} from '../io/stringtable.ts';
import { GobDrawContext } from '../render/gobdraw.ts';
import { GameRenderer, type GameRendererDeps } from './gamerenderer.ts';
import {
  EventMgr,
  newEvent,
  type Event,
  gamePaintEvent,
  appStopEvent,
  gameOverEvent,
  updateTriggersEvent,
  cancelModeEvent,
  wiAutoSelectEvent,
  runUpdatesNowEvent,
  checkGameOverEvent,
  mpEndMissionWinEvent,
  mpEndMissionLoseEvent,
  mpShowObjectivesEvent,
  connectionCloseEvent,
  showMessageEvent,
  kfRedrawDirty,
  kfRedrawBeforeTimer,
  kfRedrawMax,
  knGoSuccess,
  knGoFailure,
  knGoAbortLevel,
  knAppExit,
} from './event.ts';
import { TimerMgr, CallbackTimer } from './timer.ts';
import { Rect } from './rect.ts';
import { kfFrmDoModal, ksfxGuiFormShow, kidcOk } from './uikit.ts';
import type { FormMgr, MultiFormMgr, ModalForm } from '../render/paintengine.ts';
import { FlickScroller } from './flickscroller.ts';
import { SimUIForm as SimUIFormInput, type SimUIHost } from './fingerhandler.ts';
import type { Gob } from '../../../sim/src/gob/gob.ts';
import { kfPerfSymbolFlashing } from '../../../sim/src/player/masks.ts';
import { SimUIForm } from './simuiform.ts';
import type { MiniMapModel } from '../dom/ingame/minimap-view.ts';
import { miniMapScreenRect } from '../dom/ingame/minimap-render.ts';
import type { MiniMapGob } from '../dom/ingame/minimap-render.ts';
import type { InputUIForm } from './inputui.ts';
// ?dom HUD accessors (powerDemandMax/hudFont, below) read the live PowerControl
// + the kifntHud font through the merged structure; the rest of the control
// factory + font load now live in GameRenderer (master's extraction).
import { computeDemandMax, kifntHud, kidcMenuButton } from './inputui.ts';
import { ShowAlert } from './alertstate.ts';
import { kifntShadow } from './uikit.ts';
import {
  BuildUI,
  UnitTitleLayer,
  type BuildUIDeps,
  type UnitMenuModel,
  type BuildPanelModel,
} from './buildui.ts';
import {
  MemorySoundMgr,
  defaultOptionsState,
  type ShellEnv,
  type SoundMgr,
  type OptionsState,
} from './shellform.ts';
import { DoInGameMenu, type InGameMenuButton } from './gameoptions.ts';
import {
  MemoryPrefStore,
  LoadPreferences,
  SavePreferences,
  type PrefStore,
  type Preferences,
} from '../io/prefs.ts';
import { ClientAnimationData } from '../gfx/animdata.ts';
import {
  ksfxGameBaseUnderAttack,
  ksfxReactorPowerTooLow,
  ksfxGalaxiteWarehouseTooFull,
  ksfxGalaxMinerUnderAttack,
} from '../audio/sfx.ts';
import { disableSoundEvent, enableSoundEvent } from './event.ts';
import { Ecom, type EcomInfo } from './ecom.ts';
import { setInstructionMouseMode } from './inputverbs.ts';
import { CutScene, type CutSceneInfo } from './cutscene.ts';
import { EnqueueStoryModal } from './simpause.ts';
import {
  ShowObjectives,
  type ObjectivesDeps,
  type ObjectivesPlayer,
  type ObjectivesSummaryInfo,
} from './objectives.ts';
import { objectivesSnapshot, type ObjectivesSnapshot } from '../dom/ingame/objectives-snapshot.ts';
import { makeGameOptionsModel, type GameOptionsModel } from '../dom/ingame/game-options-io.ts';
import type { SimUIModel } from '../dom/ingame/simui-view.ts';
import { ksoObjectives, ksoWinSummary, ksoLoseSummary } from './gameoptions.ts';
import {
  HtMessageBox,
  kidfMessageBoxQuery,
  kfMbWhiteBorder,
  kfMbKeepTimersEnabled,
} from './msgbox.ts';
import { CreateLagForm, type LagFormUi, type DomLagBox } from './lagform.ts';
import type { Chatter } from './chatter.ts';
import { GetColorName } from './gameform.ts';
import { kfPlrSummaryShown, kfPlrWinner, kfPlrLoser } from './objectives.ts';
import { PickLoadGameStream, PickSaveGameStream } from './loadsave.ts';
import {
  SaveGame,
  HostNewSaveGameStream,
  HostOpenSaveGameStream,
  knGameReinitializeSave,
  type SaveStore,
  type MissionIdentifier,
} from '../io/savegame.ts';
import { SpriteManager, AnimSprite } from '../render/sprites.ts';
import {
  resetProjectileInterp,
  hasActiveProjectileInterp,
  glidePaintIntervalMs,
  kDisplayUpdateDefault,
} from '../render/projectile-interp.ts';
import type { CompleteManager } from '../io/completemgr.ts';
import {
  kfPlrComputer,
  kfPlrObserver,
  kfPlrUnfulfilled,
  kpidNeutral,
  kutMax,
  kutReactor,
} from '../../../sim/src/player/side.ts';
import type { StructConsts } from '../../../sim/src/gob/unitconsts.ts';
import { GetSideMask } from '../../../sim/src/types.ts';
import type { WPoint } from '../../../sim/src/types.ts';
import { md5bytes } from '../../../workers/leaderboard/src/md5.ts';
import { WcFromTc } from '../../../sim/src/math/coords.ts';
import { decodeMessage, encodeMessage } from '../../../sim/src/sm/message.ts';
import { FetchHttpService, kszSyncErrorUploadUrl, type HttpService } from '../net/httpservice.ts';
import { UploadByteBuffer } from '../net/uploader.ts';
import { NMID } from '../../../protocol/constants.ts';
import type { GameInner } from '../../../protocol/gamemsg.ts';

// game/game.cpp:58 — `int gtGameSpeed = kcmsUpdate / 10;` with kcmsUpdate = 80
// (ht.h:1017): 8 host ticks (1/100s) per sim update = 80 ms/update, 12.5 UPS.
export const gtGameSpeed = 80 / 10;

// game/ht.h:3934-3936 — the RunSimulation role flags. A lockstep CLIENT is
// kfRoleMultiplayer without kfRoleServer (SimUI.cpp:806 etc. test exactly that
// combination); kfRoleCreator marks the game's creator (Lobby::BeginGame).
export const kfRoleMultiplayer = 0x0001;
export const kfRoleServer = 0x0002;
export const kfRoleCreator = 0x0004;

// game/SimUI.cpp:804 `#define kctLocalLag 200` — 2s without server traffic
// raises the "Network lag" alert.
const kctLocalLag = 200;

// mpshared/netmessage.h:343-345 — the WinStats ff bits the CLIENT sets
export const kfwsWinner = 0x0002;
export const kfwsLoser = 0x0004;

// mpshared/netmessage.h:438-442
export const knDisconnectReasonResign = 0;
export const knDisconnectReasonKilled = 1;
export const knDisconnectReasonAbnormal = 2;
export const knDisconnectReasonNotResponding = 3;
export const knDisconnectReasonLeftGame = 4;

/** The transport seam the MP session sends through — XTransport satisfies it
 *  (gptra->SendNetMessage, game/xtransport.cpp:857-865). Kept narrow so the
 *  session never owns connection lifecycle (GameForm/Lobby do, M5b). */
export interface MpTransport {
  SendNetMessage(nm: GameInner): boolean;
  /** XTransport::UpdateAllies (xtransport.cpp:972-983) — the alliance notify
   *  the DO uses to route team chat (PlayerMgr::SetAllies, Player.cpp:268). */
  UpdateAllies?(side: number, sidmAllies: number): void;
}

/** See header note 2 — game.cpp:2278 FilterEvent → UpdateTriggers. */
export type TriggerPumpPolicy = 'serviced' | 'starved';

export interface GameSessionOpts {
  /** The LOADED sim (Sim.load, multiplayer:false, fog host wired). */
  sim: Sim;
  /** The art stack — share one across sessions (atlas decode dominates). */
  pakr: PackFileReader;
  tam: TexAtlasMgr;
  coords: CoordMap;
  /** game.ini — the [Fonts] table (gapfnt, game.cpp:419-431). */
  gameIni: IniReader;
  /** GobTemplates.ini sections (gpiniGobs) — the SAME array handed to Sim.load;
   *  the build UI's UnitGob::LoadMenu reads per-gt "Menu" overrides
   *  (Unit.cpp:731-748). */
  templates: IniSection[];
  /** The level's tile map name — the C++ reads it from the level ini's
   *  [General] TileMap property (game/Level.cpp:207-214), e.g. 'm_02.tmap'. */
  tmapName: string;
  /** The display size (gpdisp mode), e.g. { cx: 1024, cy: 768 }. */
  screen: { cx: number; cy: number };
  /** Renderer seam: the DibBitmap surface factory (back dib, tilemap draw
   *  surface, minimap dib). The browser host passes the WebGL2 factory here;
   *  default = the CPU reference surface (the oracle path, and node tests). */
  createSurface?: (cx: number, cy: number) => CpuDibBitmap;
  pumpPolicy?: TriggerPumpPolicy;
  /** The scripted host clock origin (HostGetTickCount, 1/100s ticks). */
  startTicks?: number;
  /** gfShowFPS. */
  fShowFPS?: boolean;
  /** Extra input-slice host seams (PopupMenu, PlaySfx, ...). */
  suiHost?: Partial<SimUIHost>;
  /** gtGameSpeed at session entry (game.cpp:58 boot value 8; the prefs apply
   *  and the in-game options slider move the global — pass the live value). */
  gtGameSpeed?: number;
  /** gsndm — defaults to the state-only MemorySoundMgr (M4b shell seam). */
  sndm?: SoundMgr;
  /** gpprefs + its file store — default in-memory (the shell session shares
   *  its own so options round-trip across shell <-> game). */
  prefs?: Preferences;
  prefStore?: PrefStore;
  /** the option globals (game.cpp:50-62) — pass the shell session's so the
   *  in-game options form mutates the same state the shell saved. */
  optionsState?: OptionsState;
  /** ShellEnv seam overrides for the in-game menu flow (saveGame,
   *  pickLoadGameStream, showObjectives, msgBox, ...). */
  menuEnv?: Partial<ShellEnv>;
  /** The save-slot store (io/savegame.ts) — enables the DEFAULT saveGame/
   *  pickLoadGameStream env arms (the in-game menu SAVE/LOAD + the summary
   *  forms' LOAD GAME). Absent = the arms stay embedder seams. */
  saveStore?: SaveStore;
  /** Game::m_miid (the mission being played) — the saveGame envelope identity
   *  + ShowObjectives' MarkComplete reach (Player.cpp:1157). */
  miid?: MissionIdentifier;
  /** gpcptm (io/completemgr.ts) — MarkComplete on a SP win. */
  completeMgr?: CompleteManager;
  /** SimUIForm::m_wfRole (kfRole*, game/ht.h:3934) — 0 (SP, the default) or
   *  kfRoleMultiplayer [| kfRoleCreator] for the lockstep-client arms. An MP
   *  session needs `sim` built by Sim.create({multiplayer:true, roster}) and a
   *  transport. */
  wfRole?: number;
  /** gptra for the MP arms (flush gcmdq / SendUpdateResult). Required when
   *  wfRole has kfRoleMultiplayer. */
  mpTransport?: MpTransport;
  /** TS-only reconnect (main.ts): called when the mid-game socket drops, to start the
   *  reconnect+resume orchestration. Absent → the legacy "Comm Problem"/abort path runs instead. */
  onReconnectNeeded?: () => void;
  /** Game::m_gameid (SimUIForm::m_gameid) — the WinStats MD5 + sync-error
   *  upload key. */
  gameid?: number;
  /** SimUIForm::m_pchatter (&chatter through RunSimulation) — the in-game
   *  chat overlay plumbing. */
  chatter?: Chatter;
  /** gphttp for the ReportSyncError upload arm (SimUI.cpp:568-572
   *  UploadByteBuffer of the StateTracker ToJson to
   *  kszSyncErrorUploadUrl?gameid=&pid=). Default: a lazy FetchHttpService over
   *  the global fetch — the WASM client's WebHttpService. Tests MUST pass a
   *  scripted service/fetch before driving a sync error (no live posts). */
  http?: HttpService;
  /** HostAnalytics (host.cpp:926-932 — the WEB build's gtag page-path call;
   *  here the /play/syncerror breadcrumb). Default: console.info in a browser
   *  (window present); silent under node. */
  hostAnalytics?: (psz: string) => void;
  /** HostGetCurrentKeyState (host.cpp:738-760, SDL_GetKeyboardState poll) —
   *  InputUIForm::OnTimer's arrow-key map scroll (InputUI.cpp:129-148). The
   *  browser host tracks keydown/keyup; default 0 = no keys held. */
  hostGetCurrentKeyState?: (keyBits: number) => number;
  /** the in-game OBJECTIVES briefing (kidfObjectives) DOM overlay. When set, the
   *  ksoObjectives ShowObjectives paths build an ObjectivesSnapshot and call this;
   *  `done` resumes (the caller's pause is held until then). The SP win/lose
   *  SUMMARIES (ksoWinSummary/ksoLoseSummary) render DOM via a SEPARATE hook
   *  (domObjectivesSummary) gated inside ShowObjectives — NOT here. DISPLAY-only:
   *  no sim/command/wire path changes. Absent (a headless harness) ⇒ no UI shown. */
  domShowObjectives?: (snapshot: ObjectivesSnapshot, done: () => void) => void;
  /** the in-game PAUSE MENU (kidfInGameMenu) DOM overlay. When set, DoInGameMenu
   *  branches to env.domInGameMenu (the visible buttons + an onSelect that routes
   *  the chosen kidc through the SAME shared action handler). main.ts mounts a
   *  PauseMenuView over the canvas root. Absent (a headless harness) ⇒ no UI shown.
   *  DISPLAY-only — the actions are the shared handler's, unchanged. */
  domInGameMenu?: (buttons: InGameMenuButton[], onSelect: (idc: number) => void) => void;
  /** the WIN / LOSE RESULTS SUMMARY DOM overlay. Threaded into shellEnv so
   *  EVERY ShowObjectives summary path (EndMission / pause-menu abort / MP) routes
   *  here. Absent (a headless harness) ⇒ no UI shown. DISPLAY-only. */
  domObjectivesSummary?: (info: ObjectivesSummaryInfo, onChoice: (idc: number) => void) => void;
  /** MP only: the server announced (ScReplayAvailable) that this just-won game's recording is in R2 at
   *  `replayKey`. The host lights up the end-of-game summary's "VIEW REPLAY" option. Absent (SP / a
   *  headless harness) ⇒ ignored. DISPLAY-only — no sim/wire change beyond reading the control message. */
  onReplayAvailable?: (replayKey: string) => void;
  /** the in-game GAME OPTIONS form (kidfInGameOptions) DOM overlay. When set,
   *  DoModalGameOptionsForm branches to env.domGameOptions; main.ts mounts a
   *  GameOptionsView over the canvas root and calls done() on OK/CANCEL. The pause-
   *  menu OPTIONS routes here. Absent (a headless harness) ⇒ no UI shown.
   *  DISPLAY-only — the apply uses the SAME env seams the canvas form used. The
   *  view receives the pure GameOptionsModel built off shellEnv (the read/apply). */
  domGameOptions?: (model: GameOptionsModel, done: () => void) => void;
  /** the pause-menu HELP (kidcHelp → FULL manual) DOM overlay. When set,
   *  handleInGameMenuChoice's kidcHelp arm branches to env.domHelpManual; main.ts mounts
   *  a HelpManualOverlayView (the SAME mountHelpManual renderer as the DOM main-menu HELP)
   *  over the paused canvas and calls done() on OK/Esc. DISTINCT from the build panels'
   *  SECTION-only domHelp. Absent (a headless harness) ⇒ no UI shown. DISPLAY-only —
   *  no sim/wire change. */
  domHelpManual?: (done: () => void) => void;
  /** the in-mission E-COM DOM overlay. When set, host.Ecom → Ecom() branches
   *  to env.domEcom (the from/to/message/large/nBackground info + a done() the host
   *  calls on dismiss). main.ts mounts an EcomView over the canvas. The DOM
   *  typewriter is browser-timer driven so it animates under the SP story-modal
   *  pause. Absent (a headless harness) ⇒ no UI shown. DISPLAY-only. */
  domEcom?: (info: EcomInfo, done: () => void) => void;
  /** the in-mission CUTSCENE DOM overlay (kidfCutScene 1047). When set,
   *  host.CutScene → CutScene() branches to env.domCutScene (the raw briefing text +
   *  a done() the host calls on dismiss). main.ts mounts a CutSceneView over the
   *  canvas. The DOM typewriter is browser-timer driven so it animates under the SP
   *  story-modal pause. Absent (a headless harness) ⇒ no UI shown. DISPLAY-only. */
  domCutScene?: (info: CutSceneInfo, done: () => void) => void;
  /** "is a DOM full-screen story overlay (the in-mission CUTSCENE) still mounted?"
   *  The DOM cutscene puts NOTHING on the canvas form stack (cutscene.ts returns
   *  before LoadForm when domCutScene is set), so WireJumpToMission's
   *  GetModalForm()===null guard sees nothing and would hand the s_00→s_01
   *  JumpToMission off WHILE the intro cutscene is still up (the briefing E-Com then
   *  overlaps it). This predicate lets the guard ALSO wait on the DOM overlay — the
   *  DOM analogue of GetModalForm(). Absent (a headless harness) ⇒ the guard waits on
   *  the canvas modal stack only. DISPLAY/sequencing-only. */
  domStoryOverlayActive?: () => boolean;
  /** the HtMessageBox family (alerts / warnings / errors / Yes-No queries) DOM
   *  overlay. Threaded into shellEnv.domMessageBox so HtMessageBox (ui/msgbox.ts)
   *  branches here for EVERY in-game box (the resign/leave WARNING via AskResignGame,
   *  the GAME OVER observe query, the MP comm/server-message errors, the objectives
   *  alert). The DOM view reports OK/Yes/No through the SAME `done` callback the canvas
   *  DoModal used (wire-identical — no Message/cmdq). Absent (a headless harness) ⇒
   *  no UI shown. DISPLAY-only — no sim/command/wire change. */
  domMessageBox?: (
    title: string,
    message: string,
    variant: 'ok' | 'query',
    whiteBorder: boolean,
    done: (fOk: boolean) => void,
  ) => void;
  /** the in-game MP "LAG ALERT" Yes/No query (SimUI.cpp OnLagNotify) DOM overlay —
   *  a DEDICATED, modeless seam (NOT the shared domMessageBox singleton, because the
   *  lag box must coexist with / outlive other boxes). Threaded into
   *  shellEnv.domLagBox; CreateLagForm (ui/lagform.ts) branches here. The box reports
   *  Yes/No through done(fOk) (the SAME CsKillLaggingPlayer vote — wire-identical) and
   *  returns its teardown closure. Absent (a headless harness) ⇒ no UI shown.
   *  DISPLAY-only. */
  domLagBox?: DomLagBox;
  /** the in-game LOAD (kidfLoadGame) / SAVE (kidfSaveGame) slot-list DOM overlay.
   *  Threaded into shellEnv (consumed by PickLoad/PickSaveGameStream); the HOST
   *  enumerates slots (saves-io) + opens the stream/writes the save. Absent (a
   *  headless harness) ⇒ no UI shown. DISPLAY-only — no sim/command/wire change. */
  domLoadGame?: (store: SaveStore, done: (slot: number | null) => void) => void;
  domSaveGame?: (store: SaveStore, done: (slot: number | null) => void) => void;
  /** the MP "WAITING FOR ALL PLAYERS…" sim-start overlay (the DOM WaitingView).
   *  Mounts the DOM overlay and RETURNS its unmount closure (torn down at the
   *  first block / teardown). */
  domWaiting?: (title: string) => () => void;
  /** the kidfAreYouSure CONFIRMATION ("Are you sure?", e.g. SELL BUILDING / ABORT
   *  BUILD) DOM overlay. Threaded into BuildUIDeps so BuildUI.PopupConfirmation
   *  calls this and routes its result to the SAME continuation (YES runs the existing
   *  command-enqueueing action; NO does nothing). main.ts mounts a ConfirmView over
   *  the canvas. COMMAND-ADJACENT, DISPLAY-only: the view emits NO command/Message —
   *  the command stays in buildui.ts. Absent (a headless harness) ⇒ no UI shown. */
  domConfirm?: (
    title: string,
    titleColor: string,
    anchor: { cx: number; cy: number } | null,
    onResult: (confirmed: boolean) => void,
  ) => void;
  /** the SECTION-ONLY in-game HELP DOM overlay. Threaded into BuildUIDeps so the
   *  build panels' "?" (kidcHelp) calls this with the context section anchor
   *  (buildings / vehicles / personnel / upgrades). main.ts mounts a HelpView OVER
   *  the build panel (it stays open underneath) that fetches /help.txt, extracts the
   *  section, and renders it. DISPLAY-only — no command/Message. Mirrors domConfirm.
   *  Absent (a headless harness) ⇒ no UI shown. */
  domHelp?: (anchor: string) => void;
  /** the gob CONTEXT MENU (UnitMenu) DOM overlay. Threaded into BuildUIDeps
   *  so BuildUI.PopupMenu calls this. The view is persistent + live-refreshing, so
   *  the seam is pull-shaped: getModel() returns the CURRENT read-model (the SAME
   *  InitMenu/SetOwner logic) or null when the menu should close; onSelect(idc)
   *  routes to the SAME OnMenuItemSelected the continuation runs; onClose() lets the
   *  view tear down (AutoTakedown). main.ts mounts a UnitMenuView anchored to the gob.
   *  COMMAND-ADJACENT, DISPLAY-only: the view emits NO command/Message — the command
   *  stays in buildui.ts. Absent (a headless harness) ⇒ no UI shown. */
  domUnitMenu?: (
    getModel: () => UnitMenuModel | null,
    onSelect: (idc: number) => void,
    onClose: () => void,
  ) => void;
  /** the UNIT BUILD carousel (kidfBuildVehicle "ORDER VEHICLE" / kidfBuildInfantry
   *  "RECRUIT PERSONNEL") DOM overlay. Threaded into BuildUIDeps so the VTS/HRC Build
   *  arm of OnMenuItemSelected SetOwner's the form (the existing BuildFormDefUpdate /
   *  BuildFormOnUnitCompleted seams keep refreshing it) and hands the view a
   *  pull-shaped model getter + action routers. getModel() returns the live read-model
   *  (or null when the builder deactivated → close); onSelect(ut) selects a unit;
   *  onOrder()/onCancelOrder() run the SAME OnControlSelected (the EXACT
   *  kmidBuildOther/kmidAbortBuildOther enqueue stays in buildui.ts); onClose() ends
   *  the form (GetLastSelection persistence). main.ts mounts a BuildPanelView. COMMAND-
   *  ADJACENT, DISPLAY-only: NO command/Message in the DOM. Absent (a headless
   *  harness) ⇒ no UI shown. */
  domBuildPanel?: (
    getModel: () => BuildPanelModel | null,
    onSelect: (ut: number) => void,
    onOrder: () => void,
    onCancelOrder: () => void,
    onClose: () => void,
    onHelp: () => void,
  ) => void;
  /** the STRUCTURE BUILD carousel (kidfBuildStructure "ORDER BUILDING") DOM overlay.
   *  Threaded into BuildUIDeps so the HQ Build arm of OnMenuItemSelected SetOwner's
   *  the form and hands the view a pull-shaped model getter + action routers.
   *  getModel() returns the live read-model (variant 'structure'); onSelect(ut)
   *  selects a structure; onBuild() runs "Build" — which ENQUEUES NOTHING: it ends the
   *  form with the selected gt and the BuildUI continuation hands gt to gpfrmPlace (the
   *  CANVAS placement overlay, FORM F not ported); onClose() ends the form
   *  (GetLastSelection persistence). main.ts mounts the SAME BuildPanelView with the
   *  structure variant. DISPLAY-only: NO command/Message in the DOM. Absent (a
   *  headless harness) ⇒ no UI shown. */
  domStructurePanel?: (
    getModel: () => BuildPanelModel | null,
    onSelect: (ut: number) => void,
    onBuild: () => void,
    onClose: () => void,
    onHelp: () => void,
  ) => void;
  /** the RESEARCH carousel (kidfUpgrade "R&D TECHNOLOGIES") DOM overlay. Threaded into
   *  BuildUIDeps so the ResearchGob kidcResearch arm of OnMenuItemSelected SetOwner's
   *  the form and hands the view a pull-shaped model getter + action routers.
   *  getModel() returns the live read-model (variant 'research' — ONLY a COST meter +
   *  PREREQUISITES + DESCRIPTION, an empty state); onSelect(upgm) selects an upgrade;
   *  onResearch() runs "Research" — which ENQUEUES NOTHING: it ends the form with the
   *  selected upgm and the BuildUI continuation enqueues kmidUpgradeCommand (wfUpgrade
   *  per choice — E-R5); onClose() ends the form. main.ts mounts the SAME
   *  BuildPanelView with the research variant. DISPLAY-only: NO command/Message in the
   *  DOM. Absent (a headless harness) ⇒ no UI shown. */
  domUpgradePanel?: (
    getModel: () => BuildPanelModel | null,
    onSelect: (upgm: number) => void,
    onResearch: () => void,
    onClose: () => void,
    onHelp: () => void,
  ) => void;
  /** the persistent in-game HUD BOTTOM BAR DOM overlay. When set, InputUIForm hides
   *  its OWN display controls and pushes the live {credits, needCredits, powerDemand,
   *  powerSupply} here per frame (the SAME getters the canvas reads). main.ts mounts
   *  a persistent HudView over the live canvas (pointer-events:none container — map
   *  gestures fall through). Absent (a headless harness) ⇒ no UI shown. DISPLAY-only —
   *  no sim/command/wire change. */
  domHud?: {
    update(state: {
      credits: number;
      needCredits: number;
      powerDemand: number;
      powerSupply: number;
      demandMax: number;
      flashOn: boolean;
      attackMoveArmed?: boolean;
    }): void;
  };
  /** the in-game OVERLAY-TEXT-LABEL DOM seam. When set, the SimUIForm suppresses the
   *  canvas paint of its 4 text labels (objective / alert / countdown / FPS) and
   *  pushes a model here per frame (read off the live controls). main.ts mounts a
   *  SimUIView over the live canvas. Absent (a headless harness) ⇒ no UI shown.
   *  DISPLAY-only. */
  domSimUI?: { update(m: SimUIModel): void };
}

export class GameSession {
  readonly sim: Sim;
  readonly coords: CoordMap;
  readonly evm: EventMgr;
  readonly gtimm: TimerMgr;
  /** The extracted RENDER stack (the display partition + HUD forms + tile/fog +
   *  sprites). GameSession composes one with its real collaborators and
   *  re-exposes its fields below so the post-ctor code stays unchanged. */
  readonly renderer: GameRenderer;
  // --- the render fields re-exposed off the renderer so the post-ctor session
  // code (alerts/HUD/draw/MP overlays) keeps reading `this.X`. The fields
  // resize() mutates (back, sizPlayfield) are GETTERS over the renderer so they
  // never go stale; the rest are stable post-construction object handles.
  get mfrmm(): MultiFormMgr {
    return this.renderer.mfrmm;
  }
  get gpfrmmSim(): FormMgr {
    return this.renderer.gpfrmmSim;
  }
  get gpfrmmInput(): FormMgr {
    return this.renderer.gpfrmmInput;
  }
  get pfrmSimUI(): SimUIForm {
    return this.renderer.pfrmSimUI;
  }
  get pfrmInputUI(): InputUIForm {
    return this.renderer.pfrmInputUI;
  }
  /** The input slice (gsim/ggobm/gpplrLocal reach + the pen-handler state). */
  readonly sui: SimUIFormInput;
  /** The build/command UI bundle (the C++ InitClass statics: HqGob::s_pfrmBuild,
   *  gpfrmPlace, Vts/HrcGob::s_pfrmBuild, gapuntc[ut]->pfrmMenu, ...). */
  readonly buildui: BuildUI;
  get tmap(): TileMap {
    return this.renderer.tmap;
  }
  /** The shared back dib (gpmfrmm's dib) — frame() exposes its rgba; resize()
   *  recreates it (Display::SetSize, display.cpp:354-366). */
  get back(): CpuDibBitmap {
    return this.renderer.back;
  }
  /** The playfield (sim strip) size — ggame.GetPlayfieldSize. */
  get sizPlayfield(): { cx: number; cy: number } {
    return this.renderer.sizPlayfield;
  }

  /** The C++ globals the in-game MENU/options flow reaches (M4b): gsndm,
   *  gpprefs (+store), the option globals, and the ShellEnv bundle the shell
   *  forms thread (gameoptions.ts/help.ts). */
  readonly sndm: SoundMgr;
  readonly prefs: Preferences;
  readonly prefStore: PrefStore;
  readonly options: OptionsState;
  readonly shellEnv: ShellEnv;
  /** gtGameSpeed (game.cpp:58) — the LIVE value (SetGameSpeed moves it). For the
   *  SP-only 11x notch this holds the fractional sentinel kt11x (8/11); the
   *  prefs writer always persists a WASM-safe integer (gameoptions.ts kt11x). */
  gtGameSpeed: number;

  // --- the SP fractional-pacing accumulator (BEYOND-SPEC; tasks/waves/f11-todo.md).
  // The timer fires at an INTEGER rate (m_ctTimerRate >= 1 tick). Per fire it
  // owes `m_ctTimerRate * m_speedNum / m_speedDen` updates; an integer-rational
  // accumulator runs floor(carry) updates and carries the remainder — exact over
  // any number of fires (no float drift). For every INTEGER game speed t:
  // speedNum=1, speedDen=t, ctTimerRate=t → exactly 1 update per fire, residual
  // carry always 0 (BIT-IDENTICAL to the pre-F11 single-fire-single-update path).
  // For the SP 11x notch (t=8/11): speedNum=11, speedDen=8, ctTimerRate=1 →
  // 88 updates per 8 ticks = 137.5 ups. Set by SetGameSpeed / the ctor.
  private m_speedNum = 1;
  private m_speedDen = 8;
  private m_ctTimerRate = 8;
  private m_accNum = 0; // the residual-fraction numerator over m_speedDen

  /** gfSuspendUpdates / gfSingleStep (game.cpp:64-66 — the pause-menu gate). */
  fSuspendUpdates = false;
  fSingleStep = false;
  /** the coalesced minimap content-dirty state (Phase 2 — forms.ini elimination).
   *  The sim's MiniMapRedraw* + CalcPoweredRadar host seams feed it (replacing gpmm's
   *  event-driven incremental dib); the DOM MiniMapView reads + clears it via
   *  minimapModel(). The seams deliver PRECISE tile coords (fogmap/Unit/Struct fire
   *  per changed tile/rect), so we accumulate a dirty tile-rect UNION instead of one
   *  boolean — the view then repaints just that rect (redrawTRect) instead of
   *  rescanning the whole map per combat frame. dirtyAll=true at start (and on
   *  MiniMapRedraw/CalcPoweredRadar/RevealAll) forces the full redraw. */
  private mmDirty: 'none' | 'rect' | 'all' = 'all';
  private readonly mmDirtyRect = { left: 0, top: 0, right: 0, bottom: 0 };

  private minimapDirtyAllSet(): void {
    this.mmDirty = 'all';
  }

  private minimapDirtyUnion(
    txLeft: number,
    tyTop: number,
    txRight: number,
    tyBottom: number,
  ): void {
    if (this.mmDirty === 'all') return;
    const r = this.mmDirtyRect;
    if (this.mmDirty === 'none') {
      r.left = txLeft;
      r.top = tyTop;
      r.right = txRight;
      r.bottom = tyBottom;
      this.mmDirty = 'rect';
      return;
    }
    if (txLeft < r.left) r.left = txLeft;
    if (tyTop < r.top) r.top = tyTop;
    if (txRight > r.right) r.right = txRight;
    if (tyBottom > r.bottom) r.bottom = tyBottom;
  }

  /** the sim playfield's update map (damage-driven repaint target) — null until the
   *  renderer exists (seams installed before it; they only fire from sim Updates,
   *  which happen after, but guard anyway). Cached after first resolve: the seam
   *  bodies fire per changed fog tile per sim update, and FormMgr keeps ONE UpdateMap
   *  for its lifetime (Init reuses it across resize — paintengine.ts). */
  private updSimCache: UpdateMap | null = null;
  private updSim(): UpdateMap | null {
    if (this.updSimCache === null) {
      this.updSimCache =
        (this.renderer as GameRenderer | undefined)?.gpfrmmSim.GetUpdateMap() ?? null;
    }
    return this.updSimCache;
  }
  /** MiniMapRedrawTRect's InvalidateMapTileRect argument — reused (the seam fires per
   *  moved unit/struct per sim update; a literal per call is pure GC churn). */
  private readonly trcSeamScratch: TRectSmall = { txLeft: 0, tyTop: 0, txRight: 0, tyBottom: 0 };
  /** Latched when the loop sees gameOverEvent (game.cpp:1827-1832). */
  fGameOver = false;
  /** gameOverEvent's dw (the knGo* outcome) — game.cpp:1828 `nGo = (int)evt.dw`;
   *  -1 until fGameOver latches. SP mission end runs the REAL modal objectives
   *  summary (ui/objectives.ts) and posts its result — knGoSuccess on a win,
   *  knGoAbortLevel on a loss unless the form's buttons say otherwise
   *  (Player.cpp:1130-1229). */
  nGo = -1;
  /** Game::m_szNextLevel (ht.h:2467) — SetNextMissionAction moves it
   *  (host.SetNextLevel); the campaign flow + the saveGame envelope read it. */
  szNextLevel = '';
  /** gpsprm (Display::GetSpriteManager) — the overlay sprite stack; lives on the
   *  renderer (it draws them) but the input-slice host seams reach it here. */
  get sprm(): SpriteManager {
    return this.renderer.sprm;
  }
  /** UnitGob::m_panispr per hilighted gob (Unit.cpp:641-656) — renderer-owned;
   *  the input-slice host seams (Create/DestroyHilightSprite) reach it here. */
  private get hilightSprites(): Map<unknown, AnimSprite> {
    return this.renderer.hilightSprites;
  }
  /** Game::m_fSkipSaveReinitialize (SkipSaveReinitializeGame, ht.h:2425). */
  private fSkipSaveReinitialize = false;
  /** the 500ms web continuous-save throttle (game.cpp:1834-1841 s_msLast). */
  private msLastReinitSave = 0;
  private readonly saveStore?: SaveStore;
  /** the DOM OBJECTIVES-briefing overlay seam (see GameSessionOpts). */
  private readonly m_domShowObjectives?: (snapshot: ObjectivesSnapshot, done: () => void) => void;
  /** "is a DOM full-screen story overlay (cutscene) up?" — WireJumpToMission
   *  consults this so the s_00→s_01 hand-off waits for the intro overlay to clear (see
   *  GameSessionOpts.domStoryOverlayActive). */
  private readonly m_domStoryOverlayActive?: () => boolean;

  private readonly pumpPolicy: TriggerPumpPolicy;
  /** SimUIForm::m_wfRole — see GameSessionOpts.wfRole. */
  readonly wfRole: number;
  private mpTransport: MpTransport | null; // re-pointed at the fresh transport on reconnect (setMpTransport)
  /** TS-only reconnect: true while the mid-game socket dropped and we're re-binding. The session stays
   *  alive (no gameOverEvent) and shows a "Reconnecting…" alert instead of "Network lag" while it holds. */
  private m_fReconnecting = false;
  /** Set by the host (main.ts): kicks off the reconnect orchestration when the socket drops mid-game. */
  private readonly m_onReconnectNeeded: (() => void) | null;
  /** SimUIForm::m_gameid. */
  private readonly m_gameid: number;
  /** MP: the host seam fired when the server announces this won game's replay is in R2 (ScReplayAvailable). */
  private readonly m_onReplayAvailable: ((replayKey: string) => void) | null;
  /** SimUIForm::m_pchatter (SimUI.cpp:175-178). */
  private readonly m_pchatter: Chatter | null;
  /** SimUIForm::m_pfrmWaitingForAllPlayers (SimUI.cpp:283-286; deleted at the
   *  first Update, SimUI.cpp:1195-1198): the DOM "WAITING FOR ALL PLAYERS…"
   *  overlay's unmount closure (set when env.domWaiting is wired). */
  private m_domWaitingUnmount: (() => void) | null = null;
  /** SimUIForm::m_pfrmLag + m_pidLagging (SimUI.cpp:320-322 reset). LagFormUi (ui/lagform.ts):
   *  a DEDICATED DOM box (domLagBox). */
  private m_pfrmLag: LagFormUi | null = null;
  private m_pidLagging: number = kpidNeutral;
  /** gphttp (the ReportSyncError upload arm) — null until first use when no
   *  opts.http was passed (the lazy FetchHttpService default). */
  private http: HttpService | null;
  private readonly hostAnalytics: (psz: string) => void;
  /** gpstrtbl (strings.bin) — the alert raises + AskResignGame text; built by
   *  the renderer (its form host's $ranktitle reads it), re-exposed here. */
  private get strtbl(): StringTable {
    return this.renderer.strtbl;
  }
  /** g_slothack's OnPlayersButton (SimUI.cpp:139-143/244-249). */
  private readonly onPlayersButton_ = (): void => {
    this.m_pchatter?.AddChat('', this.GetPlayersString(), true);
  };
  /** SimUIForm::m_fSyncError (TRACKSTATE) — latched by ReportSyncError; no
   *  state changes after. */
  m_fSyncError = false;
  /** SimUI.cpp:166/425 — host tick of the last server message (0 = none yet);
   *  drives the kctLocalLag "Network lag" alert. */
  private m_tLastCommunication = 0;
  /** SimUI.cpp:676-681/744 m_msUpdatesBlock — ms stamp of first reaching the
   *  pending block point (0 = not there yet); the latency comparand. */
  private m_msUpdatesBlock = 0;
  /** UpdateNetMessageRecord::msReceived (SimUI.cpp:592-605) — receipt stamps
   *  parallel to the driver's queued messages, consumed FIFO at dequeue. */
  private readonly msReceivedQ: number[] = [];
  /** Game::m_miid as passed in (see miid()). */
  private optsMiid?: MissionIdentifier;
  /** gpcptm (io/completemgr.ts) — MarkComplete on a SP win. */
  private completeMgr?: CompleteManager;
  private ticks: number;
  private readonly inputQ: Event[] = [];
  private readonly simTimer: CallbackTimer;
  // game/form.cpp:578-622 — the modal loops this pump is driving. Each entry is
  // one C++ DoModal stack frame (the TOP is the innermost, the one whose loop
  // would be consuming events); onLoopExit is its post-loop epilogue
  // (form.cpp:624-632 + the caller's `act on the result` continuation).
  private readonly modalStack: { pfrm: ModalForm; onLoopExit: () => void }[] = [];
  // Simulation.cpp:575/588 s_tLastAttackNotify/s_tLastPowerLowNotify — the
  // base-attack/low-power alert throttles are PROCESS-LIFETIME function statics
  // on the HOST clock (NOT saved state): 0 in a fresh client, so the FIRST
  // update where the condition holds raises the alert immediately — which is
  // why a fresh WASM client and a fresh GameSession paint the same alert at the
  // same post-load update (static-frame oracle proven). Later raises throttle
  // 30 HOST-clock seconds apart (kctIntervalAttackNotify/PowerLowNotify).
  private tLastAttackNotify = 0;
  private tLastPowerLowNotify = 0;
  private tLastStorageNotify = 0; // Processor.cpp:461 s_tLastStorageNotify

  constructor(opts: GameSessionOpts) {
    resetProjectileInterp(); // clear cosmetic projectile-interp state so gob ids never carry across games
    this.sim = opts.sim;
    this.coords = opts.coords;
    this.pumpPolicy = opts.pumpPolicy ?? 'serviced';
    this.ticks = opts.startTicks ?? 1000;
    this.gtGameSpeed = opts.gtGameSpeed ?? gtGameSpeed;
    // derive the accumulator params from the entry speed (no timer added yet)
    this.ComputeSpeedParams(this.gtGameSpeed);
    this.wfRole = opts.wfRole ?? 0;
    this.mpTransport = opts.mpTransport ?? null;
    this.m_onReconnectNeeded = opts.onReconnectNeeded ?? null;
    this.m_gameid = opts.gameid ?? 0;
    this.m_onReplayAvailable = opts.onReplayAvailable ?? null;
    this.m_pchatter = opts.chatter ?? null;
    this.http = opts.http ?? null;
    // HostAnalytics (host.cpp:926-932) reports the page path to gtag in the
    // WEB build; the TS browser host's breadcrumb is console.info. Node (no
    // window) stays silent — the noop default.
    this.hostAnalytics =
      opts.hostAnalytics ??
      ((psz: string): void => {
        if (typeof window !== 'undefined') console.info(`[analytics] ${psz}`);
      });
    // SimUI.cpp:180 — `m_ptracker = new StateTracker(m_gameid)`: the TS sim
    // pre-built the tracker, so stamp the session's gameid into it (it feeds
    // the ReportSyncError upload payload's "gameid" field).
    this.sim.ctx.tracker.SetGameId(this.m_gameid);
    // SimUI.cpp:176-178 — the chatter rides into the game with a fresh title
    if (this.m_pchatter !== null) {
      this.m_pchatter.SetChatTitle('Game Chat');
    }
    if ((this.wfRole & kfRoleMultiplayer) !== 0) {
      if (this.mpTransport === null) {
        throw new Error('GameSession: kfRoleMultiplayer requires mpTransport');
      }
      if (!this.sim.ctx.fMultiplayer) {
        throw new Error('GameSession: kfRoleMultiplayer requires a multiplayer Sim');
      }
      // The lockstep belongs to the SimDriver, but the live client services the
      // trigger pump through FilterEvent/RunUpdatesNow (game.cpp:2276 +
      // SimUI.cpp:647/655 ggame.UpdateTriggers), NOT inside the update step —
      // suppress the driver's own (re-sim-shaped) pump call permanently.
      this.sim.driver.fSuppressTriggerPump = true;
      // SendUpdateResult plumbing (SimUI.cpp:745-757 by way of the driver's
      // onUpdateResult seam): report each closed block's hash with the C++
      // latency calc — m_msUpdatesBlock (first time at the block point) minus
      // the dequeued message's receipt stamp (SimUI.cpp:703-717).
      this.sim.onBlockHash = (block, hash) => {
        const msReceived = this.msReceivedQ[0] ?? this.ticks * 10;
        const cmsLatency =
          (this.m_msUpdatesBlock !== 0 ? this.m_msUpdatesBlock : this.ticks * 10) - msReceived;
        this.SendUpdateResult(block, cmsLatency, hash);
      };
    }

    // --- the C++ globals the shell-side forms reach in-game (M4b seams)
    this.sndm = opts.sndm ?? new MemorySoundMgr();
    // glAsyncSeed is ONE stream in the C++: the sim's serialized picks
    // (Replicator.cpp:370) and the PlaySfx-site `sfxc:` resolutions must draw
    // from the same rng, in call order — hand the sim's instance over
    this.sndm.SetRngAsync?.(this.sim.ctx.rngAsync);
    this.prefStore = opts.prefStore ?? new MemoryPrefStore();
    this.prefs = opts.prefs ?? LoadPreferences(this.prefStore)!;
    // the option globals (game.cpp:50-62); fMouseMode rides the WEB force-on
    // (game.cpp:296-301) when no shell state is shared in
    if (opts.optionsState !== undefined) {
      this.options = opts.optionsState;
    } else {
      this.options = defaultOptionsState();
      this.options.fMouseMode = true; // #if defined(WEB), game.cpp:300
    }
    // Seed the instruction-copy form factor (inputverbs.ts) from the resolved
    // mouse-mode so the E-Com / objective banner / OBJECTIVES briefing rewrite
    // touch verbs → mouse verbs from the first frame. Kept live by the in-game
    // options apply (game-options-io.ts).
    setInstructionMouseMode(this.options.fMouseMode);

    // --- the event-loop substrate: gtimm over the scripted host clock, gevm
    // over the host seams (HostGetEvent = the injectable input queue;
    // HostGetMillisecondCount = ticks*10; CookEvent/CheckSetRedrawDirty reach
    // gpmfrmm exactly like the C++ globals).
    this.gtimm = new TimerMgr(() => this.ticks);
    this.evm = new EventMgr(
      {
        hostGetEvent: () => this.inputQ.shift() ?? null,
        clockMs: () => this.ticks * 10,
        cookEvent: (evt) => {
          this.mfrmm.CookEvent(evt);
        },
        checkSetRedrawDirty: () => {
          this.mfrmm.CheckSetRedrawDirty();
        },
        filterSleepTicks: (ct) => ct,
      },
      this.gtimm,
    );

    // --- the gob-draw context the input slice draws through; the renderer
    // shares this SAME instance and stamps its hilight/update-count seams onto
    // it (see GameRenderer). Built here so it's ready for the input slice below.
    const { pakr, tam, coords } = opts;
    const gctx = new GobDrawContext(pakr, tam, coords);
    this.saveStore = opts.saveStore;
    // ?dom: the OBJECTIVES-briefing overlay seam (showObjectives interception).
    this.m_domShowObjectives = opts.domShowObjectives;
    // ?dom: the "DOM story overlay still up?" predicate (WireJumpToMission's DOM guard).
    this.m_domStoryOverlayActive = opts.domStoryOverlayActive;
    // ggame.GetPlayfieldSize — the SHARED playfield-size object the input slice
    // closes over below AND the renderer fills/mutates in place (so the slice's
    // GetPlayfieldSize stays live, including the SetViewPos pin that fires during
    // the renderer's own ctor, before this.renderer is assigned). Injected into
    // the renderer; this.sizPlayfield re-exposes r.sizPlayfield (the SAME object).
    const sizPlayfield = { cx: 0, cy: 0 };
    // gpsprm + the per-gob hilight map — SHARED with the renderer (it draws/prunes
    // them; the input-slice sprite seams below write them, and those fire during
    // the renderer's form load before this.renderer is assigned). Injected; the
    // this.sprm/this.hilightSprites getters re-expose the SAME instances.
    const sprm = new SpriteManager();
    const hilightSprites = new Map<unknown, AnimSprite>();

    // --- the input slice (the C++ SimUIForm's gsim/ggobm/gpplrLocal reach) —
    // wired to the REAL gevm (scroll/selection raise kfRedrawDirty for paints).
    // Its host seams reach renderer-owned state (this.sprm/this.hilightSprites)
    // and later collaborators (this.buildui) lazily — all resolve at call-time,
    // after the renderer is built and re-exposed below. The Scroll/ResetScroll
    // sim-FormMgr seams (Simulation::SetViewPos's scroll plumbing) are RENDER
    // concerns — the renderer wires them onto this host's seam table over its
    // own gpfrmmSim (so the SetViewPos pin, which fires inside the renderer's
    // ctor, reaches a live FormMgr; see GameRenderer).
    this.sui = new SimUIFormInput({
      ctx: this.sim.ctx,
      sim: this.sim.sim,
      gctx,
      evm: this.evm,
      GetTickCount: () => this.ticks,
      GetPlayfieldSize: () => sizPlayfield,
      host: {
        // simui.cpp:2517 pgob->PopupMenu() — the in-game unit command menu
        // (this.buildui is assigned later in this ctor; events flow only after)
        PopupMenu: (pgob) => {
          this.buildui.PopupMenu(pgob);
        },
        // gpfrmPlace (the C++ global exists from HqGob::InitClass on, BEFORE
        // the in-game forms; here BuildUI is constructed after the input
        // slice, so the FingerHandler-ctor read lands on null once — the
        // SetPassOnInput(true) it wanted (fingerhandler.cpp:50-53) is
        // completed right after BuildUI construction below)
        GetPlaceForm: () => (this.buildui !== undefined ? this.buildui.gpfrmPlace : null),
        // FingerHandler::ShowUnitTitle (fingerhandler.cpp:668-690) — canvas-removal R3 step (i):
        // the hover-title flourish is an off-Form UnitMenu wrapped in a PaintLayer (UnitTitleLayer),
        // AddLayer'd to the sim region FormMgr so the paint walk renders it. null = no unit gob.
        CreateUnitTitle: (pgob: Gob) => {
          const layer = this.buildui.MakeUnitTitle(pgob);
          if (layer === null) return null;
          this.mfrmm.host.playSfx(ksfxGuiFormShow);
          return layer;
        },
        // delete m_pfrmUnitTitle (fingerhandler.cpp:57/399) — RemoveForm's the layer.
        DestroyUnitTitle: (pfrm: unknown) => {
          (pfrm as UnitTitleLayer).Dispose();
        },
        // gsndm.PlaySfx (the FingerHandler select/move/attack voices,
        // SimUI.cpp:2332-2541) — `sfxc:<n>` resolves inside the SoundMgr
        PlaySfx: (sfx) => {
          this.sndm.PlaySfx(sfx);
        },
        // gpsprm->CreateSelectionSprite() (fingerhandler.cpp:50) — the REAL
        // drag-rect overlay (render/sprites.ts), drawn at FrameComplete. The
        // sprm/hilightSprites locals are the SAME instances the renderer owns
        // (injected) — referenced directly so these seams (which fire during the
        // renderer's form load) don't depend on this.renderer being assigned.
        CreateSelectionSprite: () => sprm.CreateSelectionSprite(),
        // UnitGob::Hilight's m_panispr = CreateHilightSprite() (Unit.cpp:641 →
        // gpsprm->CreateAnimSprite(), Unit.cpp:659-662)
        CreateHilightSprite: (punt) => {
          // ReplicatorGob::CreateHilightSprite returns NULL (ht.h:8050) — the
          // multi-strip replicator never gets the hilight pulse
          if ((punt as { m_fReplicating?: boolean }).m_fReplicating !== undefined) return;
          if (!hilightSprites.has(punt)) {
            hilightSprites.set(punt, sprm.CreateAnimSprite(gctx));
          }
        },
        // delete m_panispr (Unit.cpp:654; ~SdlAnimSprite removes from the mgr)
        DestroyHilightSprite: (punt) => {
          hilightSprites.get(punt)?.Dispose();
          hilightSprites.delete(punt);
        },
        ...opts.suiHost,
      },
    });

    // --- the gtGameSpeed update timer is added at SimUIForm::Init entry
    // (SimUI.cpp:294-299 `gtimm.AddTimer(this, gtGameSpeed)`) — i.e. BEFORE
    // InputUIForm::Init adds its scroll timer; OnTimer below is the SP arm. It
    // STAYS in GameSession (it drives the sim, not the render stack) but is
    // added to gtimm BEFORE the renderer builds its forms so the forms' own
    // timers register after it (preserving the original add order). The timer
    // rate is m_ctTimerRate (== gtGameSpeed for every INTEGER speed; the SP 11x
    // notch fires every tick and paces with the accumulator).
    this.simTimer = new CallbackTimer((t) => this.OnTimer(t));
    this.gtimm.AddTimer(this.simTimer, this.m_ctTimerRate);

    // --- the RENDER stack: the display partition + HUD forms + tile/fog/sprites
    // (the GameRenderer ctor builds it in Game::StartSim order). buildDeps (below)
    // rides the renderer's tmap (GetMapSizePx/templatesIni), exposed as r.tmap.
    const rdeps: GameRendererDeps = {
      // THIS session wires the playfield damage seams (fog/galaxite/GobInvalidate,
      // below) — the renderer may ride real damage instead of full repaints
      scopedInvalidation: true,
      pakr,
      tam,
      coords,
      gameIni: opts.gameIni,
      templates: opts.templates,
      tmapName: opts.tmapName,
      screen: opts.screen,
      createSurface: opts.createSurface,
      fShowFPS: opts.fShowFPS,
      hostGetCurrentKeyState: opts.hostGetCurrentKeyState,
      gctx,
      sizPlayfield,
      sprm,
      hilightSprites,
      sui: this.sui,
      gtimm: this.gtimm,
      evm: this.evm,
      sndm: this.sndm,
      options: this.options,
      getUpdateCount: () => this.sim.sim.GetUpdateCount(),
      getGobm: () => this.sim.ctx.gobm,
      getPvar: (name) => this.sim.ctx.pvars.Get(name),
      getAnimData: (name) => this.sim.ctx.animDataFor(name),
      getTickCount: () => this.ticks,
      getHandicap: () => this.options.wfHandicap,
      getMouseMode: () => this.options.fMouseMode,
      runUpdatesNow: () => this.RunUpdatesNow(),
      showChat: () => {
        this.m_pchatter?.ShowChat();
      },
      inGameMenu: () => {
        DoInGameMenu(this.shellEnv);
      },
      // game/InputUI.cpp:258-264 — the credits-label tap opens the modal
      // objectives form (the caller handles the SP pause around it). ?dom routes
      // ksoObjectives to the DOM overlay (showObjectivesBriefing).
      showObjectives: (done) => {
        this.showObjectivesBriefing(
          undefined,
          false,
          false,
          done === undefined ? null : () => done(),
        );
      },
      // the persistent HUD bottom bar. When set, the renderer threads this into its
      // InputUIForm — the form hides its own credits/power/menu controls + pushes the
      // live values here per frame (Update). main.ts mounts the DOM HudView.
      // Absent (a headless harness) ⇒ no UI shown.
      domHud: opts.domHud,
      // the in-game overlay text labels (objective / alert / countdown / FPS). When
      // set, the renderer threads this into the SimUIForm — the form suppresses its
      // label canvas paint + pushes the model each frame. main.ts mounts the DOM
      // SimUIView. Absent (a headless harness) ⇒ no UI shown.
      domSimUI: opts.domSimUI,
      // Form::DoModal's nested event loop dissolves into THIS session's pump:
      // the form goes on the modal stack and advanceTicks runs the per-event
      // arms; the epilogue fires when kfFrmDoModal clears.
      runModalLoop: (pfrm, onLoopExit) => {
        this.modalStack.push({ pfrm, onLoopExit });
      },
    };
    this.renderer = new GameRenderer(rdeps);

    // --- the build/command UI (the C++ InitClass statics: Headquarters.cpp:
    // 48-91, vts.cpp:27-45, HRC.cpp:29-47, Struct.cpp InitClass, UnitGob::
    // LoadMenu) — the form preloads + per-gob menu dispatch bundle. The
    // FlickScroller factory mirrors the renderer's (same evm + clock).
    const newFlickScroller = () => new FlickScroller(this.evm, () => this.ticks * 10);
    const buildDeps: BuildUIDeps = {
      ctx: this.sim.ctx,
      sui: this.sui,
      gctx,
      gtimm: this.gtimm,
      mfrmm: this.mfrmm,
      gpfrmmSim: this.gpfrmmSim,
      templatesIni: new IniReader(opts.templates),
      GetPlayfieldSize: () => this.sizPlayfield,
      GetMapSizePx: () => this.tmap.GetMapSize(),
      // Phase 2 (forms.ini elimination): the minimap's on-screen rect (the canvas
      // pfrmMiniMap->GetRect the placement dead-zone read). The form is gone — compute it
      // from the SAME geometry the DOM radar uses (miniMapScreenRect, anchored bottom-right
      // of the sim strip), so the dead zone still excludes the radar corner. Pixel-exact:
      // identical math to the old MiniMapControl rect.
      getMiniMapRect: () => {
        const sizMap = this.tmap.GetMapSize();
        const sizDib = this.back.GetSize();
        return miniMapScreenRect(
          sizDib.cx,
          this.sizPlayfield.cy,
          sizMap.cx,
          sizMap.cy,
          this.coords.gcxTile,
          this.coords.gcyTile,
          this.minimapScale(),
        );
      },
      // punt->GetAnimSprite()->GetBounds() (Unit.cpp:1180-1185) — null = the
      // C++ NULL-sprite arm (not hilighted)
      GetAnimSpriteBounds: (punt) => this.hilightSprites.get(punt)?.GetBounds() ?? null,
      PlaySfx: (sfx) => {
        this.sui.host.PlaySfx(sfx);
      },
      SetRedrawFlags: (wf) => this.evm.SetRedrawFlags(wf),
      newFlickScroller,
      // route the kidfAreYouSure confirmation to the DOM overlay.
      // Absent (a headless harness) ⇒ no UI shown. DISPLAY-only — no command path change.
      domConfirm: opts.domConfirm,
      // route the build panels' "?" HELP (section-only) to the DOM overlay.
      // Absent (a headless harness) ⇒ no UI shown.
      domHelp: opts.domHelp,
      // route the gob CONTEXT MENU (UnitMenu) to the DOM overlay.
      // Absent (a headless harness) ⇒ no UI shown. DISPLAY-only — no command path change.
      domUnitMenu: opts.domUnitMenu,
      // route the UNIT BUILD carousel (VTS/HRC) to the DOM overlay.
      // Absent (a headless harness) ⇒ no UI shown.
      domBuildPanel: opts.domBuildPanel,
      // route the STRUCTURE BUILD carousel (HQ) to the DOM overlay.
      // Absent (a headless harness) ⇒ no UI shown. "Build" returns a gt → CANVAS gpfrmPlace handoff.
      domStructurePanel: opts.domStructurePanel,
      // route the RESEARCH carousel (research center) to the DOM overlay.
      // Absent (a headless harness) ⇒ no UI shown. "Research" returns the upgm → the
      // gob-menu continuation enqueues kmidUpgradeCommand (E-R5).
      domUpgradePanel: opts.domUpgradePanel,
    };
    this.buildui = new BuildUI(buildDeps);
    // fingerhandler.cpp:50-53 — only the FingerHandler tells the placement form
    // to pass input through (so the handler can scroll the playfield); the
    // StylusHandler leaves the placement form handling clicks directly
    // (click-anywhere-to-place). The handler is chosen in SimUIForm.SetUIType
    // (gfLassoSelection || gfMouseMode -> StylusHandler), which ran during
    // SimUIForm construction before BuildUI/gpfrmPlace existed, so its
    // GetPlaceForm() read landed on null; set the correct initial state here.
    this.buildui.gpfrmPlace.SetPassOnInput(
      !(this.options.fLassoSelection || this.options.fMouseMode),
    );

    // --- the ShellEnv bundle the in-game MENU/options/help forms thread
    // (gameoptions.ts/help.ts ↔ the gpmfrmm/gpiniForms/gsndm/gsim/ggame
    // globals). nScrollSpeed/wfPerfOptions are accessors over the LIVE storage
    // (gnScrollSpeed lives on the input slice, fingerhandler.ts:288-289;
    // gwfPerfOptions on the sim context — the SP sim consumes it directly,
    // matching the C++ SP global write).
    this.sui.gnScrollSpeed = this.options.nScrollSpeed; // gnScrollSpeed applied
    // kfPerfSymbolFlashing is FORCED ON: the renderer draws the needs-power/repair/
    // need-credits symbols unconditionally (gobdraw pins gwfPerfOptions), but the
    // SIM's MarkRedraw for each symbol flip is gated on THIS mask — if prefs carried
    // the bit off, the flips would go undamaged under scoped invalidation (frozen
    // mid-blink symbols). One source of truth: the pinned renderer.
    this.sim.ctx.wfPerfOptions = (this.options.wfPerfOptions | kfPerfSymbolFlashing) & 0xffff;
    const sui = this.sui;
    const simCtx = this.sim.ctx;
    const baseOptions = this.options;
    const liveOptions: OptionsState = {
      get fLassoSelection() {
        return baseOptions.fLassoSelection;
      },
      set fLassoSelection(v) {
        baseOptions.fLassoSelection = v;
      },
      get fMouseMode() {
        return baseOptions.fMouseMode;
      },
      set fMouseMode(v) {
        baseOptions.fMouseMode = v;
      },
      get nScrollSpeed() {
        return sui.gnScrollSpeed;
      },
      set nScrollSpeed(v) {
        sui.gnScrollSpeed = v;
        baseOptions.nScrollSpeed = v;
      },
      get wfHandicap() {
        return baseOptions.wfHandicap;
      },
      set wfHandicap(v) {
        baseOptions.wfHandicap = v;
      },
      get nHueOffset() {
        return baseOptions.nHueOffset;
      },
      set nHueOffset(v) {
        baseOptions.nHueOffset = v;
      },
      get nSatMultiplier() {
        return baseOptions.nSatMultiplier;
      },
      set nSatMultiplier(v) {
        baseOptions.nSatMultiplier = v;
      },
      get nLumOffset() {
        return baseOptions.nLumOffset;
      },
      set nLumOffset(v) {
        baseOptions.nLumOffset = v;
      },
      get wfPerfOptions() {
        return simCtx.wfPerfOptions;
      },
      set wfPerfOptions(v) {
        simCtx.wfPerfOptions = (v | kfPerfSymbolFlashing) & 0xffff; // renderer-pinned, see ctor
        baseOptions.wfPerfOptions = v & 0xffff;
      },
      get nDemoRank() {
        return baseOptions.nDemoRank;
      },
      set nDemoRank(v) {
        baseOptions.nDemoRank = v;
      },
    };
    this.shellEnv = {
      mfrmm: this.mfrmm,
      evm: this.evm,
      gtimm: this.gtimm,
      pakr,
      sndm: this.sndm,
      prefs: this.prefs,
      savePreferences: () => {
        SavePreferences(this.prefs, this.prefStore, this.sndm, liveOptions, this.gtGameSpeed);
      },
      options: liveOptions,
      GetPlayfieldSize: () => this.sizPlayfield,
      getSafeAreas: () => ({ left: 0, top: 0, right: 0, bottom: 0 }),
      fMultiplayer: this.sim.ctx.fMultiplayer,
      sim: {
        Pause: (fPause: boolean) => {
          this.sim.sim.Pause(fPause);
        },
      },
      getSimUIForm: () => this.pfrmSimUI,
      plrLocal: this.sim.ctx.plrLocal,
      gameSpeed: () => this.gtGameSpeed,
      setGameSpeed: (t: number) => this.SetGameSpeed(t),
      // mirrors the renderer's HelpControlDeps.loadAnimationData (the HELP +
      // ecom anir loads share the same shipped-pack reader)
      loadAnimationData: ((pszName: string) => {
        const panid = new ClientAnimationData();
        if (!panid.Init(pakr, tam, pszName)) return null;
        return panid;
      }) as ShellEnv['loadAnimationData'],
      // ---- the M4c modal-form arms (embedder-overridable via opts.menuEnv):
      // HtMessageBox (misc.cpp:471), Player::ShowObjectives (Player.cpp:1130),
      // and — when a SaveStore rides in — Game::SaveGame(NULL)'s
      // PickSaveGameStream arm (game.cpp:2438-2442) + PickLoadGameStream.
      msgBox: (pszTitle: string, pszText: string) => {
        HtMessageBox(this.shellEnv, 0, pszTitle, pszText);
      },
      showObjectives: (
        so: number,
        fForceInfoDisplay = false,
        fAborting = false,
        done?: (nGo: number) => void,
      ) => {
        // ?dom intercepts the in-game briefing (ksoObjectives) HERE; the SP win/lose
        // SUMMARY forms render DOM via domObjectivesSummary, gated INSIDE ShowObjectives
        // (below) — so they flow through the normal ShowObjectives call. The briefing
        // resolves knGoSuccess on close (ShowObjectives' ksoObjectives default).
        if (so === ksoObjectives && this.m_domShowObjectives !== undefined) {
          this.showObjectivesBriefing(
            undefined,
            fForceInfoDisplay,
            fAborting,
            done === undefined ? null : () => done(knGoSuccess),
          );
          return;
        }
        ShowObjectives(
          this.shellEnv,
          this.objectivesDeps(),
          so,
          fForceInfoDisplay,
          fAborting,
          done ?? null,
        );
      },
      // when set, DoInGameMenu branches to the DOM PAUSE MENU overlay.
      // Presentation-only — the selected kidc routes through the shared
      // handleInGameMenuChoice.
      domInGameMenu: opts.domInGameMenu,
      // when set, ShowObjectives renders the WIN/LOSE SUMMARY as a DOM overlay.
      // Presentation-only — the chosen button routes through the shared post-modal
      // handler (the SAME gameOverEvent nGo). Briefing (ksoObjectives) is unaffected.
      domObjectivesSummary: opts.domObjectivesSummary,
      // when set, DoModalGameOptionsForm branches to the DOM GAME OPTIONS overlay.
      // Presentation-only — the pure read/apply model is built off THIS shellEnv (the
      // SAME seams the canvas InGameOptionsForm used), so the apply hits the running
      // game identically.
      domGameOptions:
        opts.domGameOptions === undefined
          ? undefined
          : (done: () => void) => {
              opts.domGameOptions!(makeGameOptionsModel(this.shellEnv), done);
            },
      // when set, the pause-menu HELP renders the FULL manual as a DOM overlay.
      // Presentation-only — no sim/wire change.
      domHelpManual: opts.domHelpManual,
      // when set, Ecom() renders the in-mission E-Com as a DOM overlay — INSIDE the
      // story-modal queue (so the SP pause + sequencing are unchanged). The
      // browser-timer typewriter animates under the pause. Presentation-only — no
      // sim/command/wire change.
      domEcom: opts.domEcom,
      // when set, CutScene() renders the in-mission cutscene as a DOM overlay
      // (kidfCutScene 1047) — INSIDE the story-modal queue (so the SP pause +
      // sequencing are unchanged). The browser-timer typewriter animates under the
      // pause. Presentation-only.
      domCutScene: opts.domCutScene,
      // when set, HtMessageBox renders the alerts / warnings / errors / Yes-No queries
      // (the resign/leave WARNING, the GAME OVER observe query, the MP comm/server-
      // message errors) as a DOM overlay. The choice routes through the SAME `done`
      // callback — wire-identical (no Message/cmdq); presentation-only.
      domMessageBox: opts.domMessageBox,
      // route the MP "LAG ALERT" Yes/No to its DEDICATED DOM overlay (CreateLagForm,
      // ui/lagform.ts).
      domLagBox: opts.domLagBox,
      // the in-game LOAD/SAVE slot-list overlays — consumed by PickLoad/PickSaveGameStream
      // (the saveGame/pickLoadGameStream arms below pass this.shellEnv, which carries these).
      domLoadGame: opts.domLoadGame,
      domSaveGame: opts.domSaveGame,
      saveGame:
        opts.saveStore === undefined
          ? undefined
          : (done?: () => void) => {
              // Game::SaveGame(NULL) — the modal slot picker, then the envelope
              PickSaveGameStream(
                this.shellEnv,
                opts.saveStore!,
                () => this.levelTitle(),
                (pstm) => {
                  if (pstm !== null) {
                    SaveGame(
                      {
                        miid: this.miid(),
                        szNextLevel: this.szNextLevel,
                        saveState: () => this.sim.saveState(),
                      },
                      pstm,
                    );
                  }
                  if (done !== undefined) done();
                },
              );
            },
      pickLoadGameStream:
        opts.saveStore === undefined
          ? undefined
          : (done: (pstm: Stream | null) => void) => {
              PickLoadGameStream(this.shellEnv, opts.saveStore!, done);
            },
      // ---- the M5b MP arms: m_pchatter->ShowChat (InputUI.cpp kidcChat),
      // ggame.AskResignGame / ggame.AskObserveGame (game.cpp:1864-1940)
      showChat: () => {
        this.m_pchatter?.ShowChat();
      },
      askResignGame: (done: (fYes: boolean) => void) => {
        this.AskResignGame(true, done);
      },
      askObserveGame: (done: (fYes: boolean) => void) => {
        this.AskObserveGame(done);
      },
      ...opts.menuEnv,
    };
    this.optsMiid = opts.miid;
    this.completeMgr = opts.completeMgr;

    // --- the sim's CLIENT-side host seams (the C++ globals these calls reach):
    // gsndm.PlaySfx — the sim-side raises (fire/damaged/destroyed/ready voices
    // across game/*.cpp; `sfxc:<n>` = the call-site SfxFromCategory pick,
    // resolved inside the SoundMgr over the ASYNC rng — sim-quirks B19)
    this.sim.ctx.host.PlaySfx = (sfx: number | string) => {
      this.sndm.PlaySfx(sfx);
    };
    // PlayerMgr::SetAllies' server notify (Player.cpp:268-271: `if (gptra !=
    // NULL) gptra->UpdateAllies(side, sidmAllies)`) — the DO consumes it to
    // route TEAM chat (workers/multiplayer endpoint → game.ts setAllies). SP
    // has no transport, exactly the C++ gptra==NULL arm.
    this.sim.ctx.host.UpdateAllies = (side: number, sidm: number) => {
      this.mpTransport?.UpdateAllies?.(side, sidm);
    };
    // Game::ScheduleUpdateTriggers posts updateTriggersEvent so GetEvent has an
    // event to return and FilterEvent gets a pass (game.cpp:2244-2255)
    this.sim.ctx.host.ScheduleUpdateTriggers = () => {
      this.evm.PostEventType(updateTriggersEvent);
    };
    // Unit.cpp:683 gevm.SetRedrawFlags(kfRedrawBeforeTimer)
    this.sim.ctx.host.SetRedrawBeforeTimer = () => {
      this.evm.SetRedrawFlags(kfRedrawBeforeTimer);
    };
    // Unit.cpp:399-403 — close the unit's menu form on deactivation/death
    this.sim.ctx.host.CloseUnitMenu = (gob: unknown) => {
      this.buildui.CloseUnitMenu(gob as Gob);
    };
    // MobileBuilder.cpp:209 pfrmBuild->DefUpdate(this, IsBuildInProgress())
    this.sim.ctx.host.BuildFormDefUpdate = (gob: unknown, fBuildInProgress: boolean) => {
      this.buildui.BuildFormDefUpdate(gob as Gob, fBuildInProgress);
    };
    // MobileBuilder.cpp:184 pfrmBuild->OnUnitCompleted(this, ut)
    this.sim.ctx.host.BuildFormOnUnitCompleted = (gob: unknown, ut: number) => {
      this.buildui.BuildFormOnUnitCompleted(gob as Gob, ut);
    };
    // Headquarters.cpp:178-186 + MobileBuilder.cpp:71-77 — Deactivate takes
    // down the build/place forms this builder owns
    this.sim.ctx.host.BuilderDeactivate = (gob: unknown) => {
      this.buildui.BuilderDeactivate(gob as Gob);
    };
    // Simulation.cpp:573-590 ShowAlert — the HUD alert label (alertcontrol.cpp).
    // Phase 1: routes to the SimUIForm's CANONICAL AlertState (the single source of
    // truth feeding the DOM push).
    this.sim.ctx.host.ShowAlert = (psz: string) => {
      ShowAlert(this.pfrmSimUI.alertState, psz);
    };
    // triggermgr.cpp:549-552 CountdownTimer::ShowTimer — the HUD countdown label.
    // Phase 1: toggles the canonical countdownVisible holder.
    this.sim.ctx.host.ShowCountdownTimer = (fShow: boolean) => {
      this.pfrmSimUI.countdownVisible = fShow;
    };
    // triggermgr.cpp:576-592 CountdownTimer::UpdateString — "%02d:%02d" into
    // the timer's format string, then (Phase 1) the canonical countdownText holder.
    this.sim.ctx.host.UpdateCountdownString = (secs: number, pszFormat: string) => {
      const cMins = Math.trunc(secs / 60);
      const cSecs = secs % 60;
      const szTemp = `${cMins > 9 ? '' : '0'}${cMins}:${cSecs > 9 ? '' : '0'}${cSecs}`;
      const szT = pszFormat.includes('%s') ? pszFormat.replace('%s', szTemp) : szTemp; // sprintf(szT, m_szFormat, szTemp)
      this.pfrmSimUI.countdownText = szT;
    };
    // Simulation.cpp:570-592 — the base-attack/low-power raises (gpstrtbl text,
    // game.cpp:436-440 strings.bin): the sim fires the seam every update the
    // condition holds; the THROTTLE is host presentation (see the field note).
    this.sim.ctx.host.NotifyBaseUnderAttack = () => {
      // kctIntervalAttackNotify = 30*100 (Simulation.cpp:572); abs as the C++
      if (
        this.tLastAttackNotify === 0 ||
        Math.abs(this.ticks - this.tLastAttackNotify) >= 30 * 100
      ) {
        this.tLastAttackNotify = this.ticks;
        this.sndm.PlaySfx(ksfxGameBaseUnderAttack); // Simulation.cpp:578
        ShowAlert(this.pfrmSimUI.alertState, this.strtbl.GetString(kidsBaseUnderAttack) ?? '');
      }
    };
    this.sim.ctx.host.NotifyPowerLow = () => {
      // kctIntervalPowerLowNotify = 30*100 (Simulation.cpp:585)
      if (this.tLastPowerLowNotify === 0 || this.ticks - this.tLastPowerLowNotify >= 30 * 100) {
        this.tLastPowerLowNotify = this.ticks;
        this.sndm.PlaySfx(ksfxReactorPowerTooLow); // Simulation.cpp:591
        ShowAlert(this.pfrmSimUI.alertState, this.strtbl.GetString(kidsLowPower) ?? '');
      }
    };
    this.sim.ctx.host.NotifyMinerUnderAttack = () => {
      // Miner.cpp:428-431 — the sim gates on the local player AND the
      // once-per-attack m_fMinerUnderAttack flag; no extra throttle here
      this.sndm.PlaySfx(ksfxGalaxMinerUnderAttack);
      ShowAlert(this.pfrmSimUI.alertState, this.strtbl.GetString(kidsMinerUnderAttack) ?? '');
    };
    this.sim.ctx.host.NotifyStorageFull = () => {
      // kctIntervalStorageNotify = 30*100 (Processor.cpp:459-466; the sim
      // already gated on the local player)
      if (this.tLastStorageNotify === 0 || this.ticks - this.tLastStorageNotify >= 30 * 100) {
        this.tLastStorageNotify = this.ticks;
        this.sndm.PlaySfx(ksfxGalaxiteWarehouseTooFull); // Processor.cpp:462
        ShowAlert(this.pfrmSimUI.alertState, this.strtbl.GetString(kidsWarehouseTooFull) ?? '');
      }
    };
    // SP EndMissionAction (TriggerActions.cpp:412-433): the sim already disabled
    // the TriggerMgr; the C++ then pauses, runs the MODAL objectives summary
    // (Player::ShowObjectives, ui/objectives.ts — win/lose summary forms), and
    // posts gameOverEvent with its return in dw — the game loop latches it and
    // exits (game.cpp:1825-1832). The C++ blocks inside the modal; here the
    // post rides the continuation (the dissolved-modal rule).
    this.sim.ctx.host.EndMission = (fWin: boolean, side: number) => {
      this.sim.sim.Pause(true);
      ShowObjectives(
        this.shellEnv,
        this.objectivesDeps(side),
        fWin ? ksoWinSummary : ksoLoseSummary,
        false,
        false,
        (nGo) => {
          this.sim.sim.Pause(false);
          const evt = newEvent(gameOverEvent);
          evt.dw = nGo;
          this.evm.PostEvent(evt);
        },
      );
    };
    // ShowObjectivesAction (TriggerActions.cpp:1127-1157): the SP arm pauses
    // around the modal objectives form; the MP arm posts mpShowObjectivesEvent
    // so TriggerMgr::Update (synchronous inside the sim update) never blocks —
    // FilterEvent's arm shows the form WITHOUT pausing.
    this.sim.ctx.host.ShowObjectives = (side: number) => {
      if (this.sim.ctx.fMultiplayer) {
        const evt = newEvent(mpShowObjectivesEvent);
        evt.dw = side;
        this.evm.PostEvent(evt);
        return;
      }
      this.sim.sim.Pause(true);
      this.showObjectivesBriefing(side, true, false, () => {
        this.sim.sim.Pause(false);
      });
    };
    // EndMissionAction's MP notify (TriggerActions.cpp:378-402, by way of the
    // sim's host.NotifyEndMission): the LOCAL player's end posts the async
    // mpEndMissionWin/Lose event; a NON-local LOSS posts checkGameOverEvent so
    // the local player can check if it won.
    this.sim.ctx.host.NotifyEndMission = (side: number, fWin: boolean) => {
      const pplr = this.sim.ctx.plrm.GetPlayer(side);
      if (pplr === this.sim.ctx.plrLocal) {
        const evt = newEvent(fWin ? mpEndMissionWinEvent : mpEndMissionLoseEvent);
        evt.dw = side;
        this.evm.PostEvent(evt);
      } else if (!fWin) {
        const evt = newEvent(checkGameOverEvent);
        evt.dw = pplr?.GetId() ?? 0;
        this.evm.PostEvent(evt);
      }
    };
    // EcomAction::Perform (TriggerActions.cpp:509-518): the E-Com overlay
    // (ui/ecom.ts); EcomSuppressed is the build-form-up read the sim gates on.
    this.sim.ctx.host.Ecom = (
      nCharFrom: number,
      nCharTo: number,
      pszMessage: string,
      nBackground: number,
      fMore: boolean,
    ) => {
      Ecom(this.shellEnv, nCharFrom, nCharTo, pszMessage, nBackground, fMore);
    };
    this.sim.ctx.host.EcomSuppressed = () => this.mfrmm.EcomSuppressed();
    // CutSceneAction::Perform (TriggerActions.cpp:1213-1224): SP-only; pauses
    // the sim around the modal (ui/cutscene.ts).
    this.sim.ctx.host.CutScene = (pszMessage: string) => {
      CutScene(this.shellEnv, pszMessage, true);
    };
    // CenterViewAction::Perform's client slice (TriggerActions.cpp:292-317):
    // area rect -> world center -> SetViewPos through the input slice (the
    // local-side gate already ran in the sim's Perform).
    this.sim.ctx.host.CenterViewOnArea = (nArea: number) => {
      const plrLocal = this.sim.ctx.plrLocal;
      if (plrLocal === null) return;
      const trc = this.sim.ctx.gobm.GetAreaRect(nArea, plrLocal.GetSide());
      // WRect::FromTileRect (misc.cpp:417-425)
      const wleft = WcFromTc(trc.left);
      const wtop = WcFromTc(trc.top);
      const wWidth = WcFromTc(trc.right) - wleft;
      const wHeight = WcFromTc(trc.bottom) - wtop;
      const rcSimUI = new Rect();
      this.pfrmSimUI.GetRect(rcSimUI);
      this.sui.SetViewPos(
        wleft + Math.trunc(wWidth / 2) - Math.trunc(this.coords.WcFromPc(rcSimUI.Width()) / 2),
        wtop + Math.trunc(wHeight / 2) - Math.trunc(this.coords.WcFromPc(rcSimUI.Height()) / 2),
        true,
      );
    };
    // SetNextMissionAction (TriggerActions.cpp:319-340) — ggame.SetNextLevel
    this.sim.ctx.host.SetNextLevel = (pszLevel: string) => {
      this.szNextLevel = pszLevel;
    };
    // SimUI.cpp:2960 gpmm->CalcPoweredRadar() — radar power gain/loss repaint. Alters
    // which enemy blips the RADAR shows across the whole map (no single tile), so it
    // forces the full-dib redraw. Minimap-only (C++ parity: gpmm->Redraw, no gpupdSim).
    this.sim.ctx.host.CalcPoweredRadar = () => {
      this.minimapDirtyAllSet();
    };
    // the minimap-CONTENT recompute seams (fogmap.cpp/Unit.cpp/MobileUnit.cpp/Struct.cpp/
    // infantry/Andy — see context.ts): the C++ minimap dib was event-driven incremental,
    // updated ONLY at these sim mutation points. The seams carry exact tile coords, so
    // they feed two render consumers here:
    //  1. the DOM minimap's dirty tile-rect union (minimapModel().dirtyRect → the view
    //     repaints just that rect instead of rescanning the whole map), and
    //  2. the PLAYFIELD update map — restoring the C++'s damage-driven invalidation
    //     (fogmap.cpp:575/594 gpupdSim->InvalidateTile and the reveal arms) that the
    //     port had parked on the per-frame full InvalidateRect(null) (now scoped, see
    //     GameRenderer displayFrameStart). Fog/galaxite transitions must damage the
    //     playfield or stale fog lingers under the fog overlay pass.
    // context.ts's host interface + the sim call sites are UNCHANGED.
    this.sim.ctx.host.MiniMapRedraw = () => {
      this.minimapDirtyAllSet();
      this.updSim()?.InvalidateRect(null);
    };
    this.sim.ctx.host.MiniMapRedrawTile = (tx: number, ty: number) => {
      this.minimapDirtyUnion(tx, ty, tx + 1, ty + 1);
      this.updSim()?.InvalidateTile(tx, ty);
    };
    this.sim.ctx.host.MiniMapRedrawTRect = (
      txLeft: number,
      tyTop: number,
      txRight: number,
      tyBottom: number,
    ) => {
      this.minimapDirtyUnion(txLeft, tyTop, txRight, tyBottom);
      const trc = this.trcSeamScratch; // consumed synchronously by InvalidateMapTileRect
      trc.txLeft = txLeft;
      trc.tyTop = tyTop;
      trc.txRight = txRight;
      trc.tyBottom = tyBottom;
      this.updSim()?.InvalidateMapTileRect(trc);
    };
    // Gob::Invalidate() (GameObjects.cpp:267) — damage the gob's last-painted bounds.
    // Fired by GobMgr::RemoveGob (a removed gob's pixels linger otherwise — shots/
    // explosion anims are the high-churn case) and MinerGob::Hide both branches
    // (dock/undock at a Processor: the hide must erase, the unhide must repaint —
    // the paint's overflow-marking pass re-marks the reappearing gob).
    this.sim.ctx.host.GobInvalidate = (pgob: unknown) => {
      const trc = (pgob as { m_trcBoundingLast?: TRectSmall }).m_trcBoundingLast;
      if (trc !== undefined) this.updSim()?.InvalidateMapTileRect(trc);
    };

    // Simulation::HandlePlayerDisconnect (Simulation.cpp:609-625): a relayed
    // kmidPlayerDisconnect executing INSIDE the update re-routes to the game
    // callback as an ScPlayerDisconnect NetMessage — i.e. the roster mutation
    // runs at the lockstep-synchronized point on every client.
    this.sim.ctx.host.OnPlayerDisconnect = (pid: number, nReason: number) => {
      this.OnPlayerDisconnectMessage(pid, nReason);
    };

    if ((this.wfRole & kfRoleMultiplayer) === 0) {
      // --- the first-update hack (game.cpp:2073-2081): SP-ONLY (`if ((wfRole &
      // kfRoleMultiplayer) == 0) m_pfrmSimUI->OnTimer(0)`): the Simulation's
      // first Update happens BEFORE its first paint — the MissionLoaded trigger
      // gets to determine the first image the player sees. An MP sim stays at
      // update -1 until the server's first block arrives.
      this.OnTimer(0);
    } else {
      // SimUIForm::Init's MP arm (SimUI.cpp:280-286): the DOM overlay shows
      // "WAITING FOR ALL PLAYERS..." — its unmount closure is fired at the first
      // block / teardown (SimUI.cpp:1195-1198, in OnTimer below). No DOM seam
      // wired (e.g. a headless harness) ⇒ no overlay.
      if (opts.domWaiting !== undefined) {
        this.m_domWaitingUnmount = opts.domWaiting('WAITING FOR ALL PLAYERS...');
      }
      // SimUIForm::Init's MP arm (SimUI.cpp:311-315): the lockstep starts at
      // block 0 and the client announces liveness with a (0,0,0) update result
      // — the DO's begin gate counts these before its timer starts ticking.
      this.msReceivedQ.length = 0;
      this.m_msUpdatesBlock = 0;
      this.SendUpdateResult(0, 0, 0);
    }

    // g_slothack.Connect (SimUI.cpp:118-147 + :188): the chatter's blink
    // signal is a no-op in-game (the #if 0 arm, SimUI.cpp:234-242); the
    // PLAYERS button adds the players string to chat.
    if (this.m_pchatter !== null) {
      this.m_pchatter.connectPlayers(this.onPlayersButton_);
    }

    // GameLoop entry (game.cpp:1800 `m_fUpdateTriggers = false;`): a trigger
    // pump scheduled during the post-load/first update is deterministically
    // KILLED before it can service — the proven dropped-pump seam the pixel
    // oracle models with SimDriver.fSuppressTriggerPump. (MP: nothing has
    // scheduled one yet — the clear is a no-op, as in the C++.)
    this.sim.sim.m_fUpdateTriggersPending = false;
  }

  /** The scripted host clock (HostGetTickCount, 1/100s ticks). */
  ticksNow(): number {
    return this.ticks;
  }

  /** Game::m_miid — the mission identity (the saveGame envelope + the
   *  MarkComplete key). Sessions loaded from a save synthesize it from the
   *  level filename + PACKID_MAIN when the embedder passed none. */
  miid(): MissionIdentifier {
    if (this.optsMiid !== undefined) return this.optsMiid;
    const plvl = this.sim.sim.GetLevel() as unknown as { m_szFileLevel?: string } | null;
    return {
      packid: { id: 0xffffffff, hash: new Uint8Array(16) }, // PACKID_MAIN
      szLvlFilename: plvl?.m_szFileLevel ?? '',
    };
  }

  /** gsim.GetLevel()->GetTitle() (Level.cpp [General] Title). */
  levelTitle(): string {
    const plvl = this.sim.sim.GetLevel() as unknown as { m_szTitle?: string } | null;
    return plvl?.m_szTitle ?? '';
  }

  /** ksoObjectives ShowObjectives. When opts.domShowObjectives is set, build the
   *  read-model off objectivesDeps() and route the in-game briefing to the DOM
   *  overlay. The caller's pause is held until `done()` (the DOM overlay calls it on
   *  OK). DISPLAY-only — no sim/command/wire change. ONLY ksoObjectives goes through
   *  here; the SP win/lose summaries call ShowObjectives directly, which renders them
   *  DOM via domObjectivesSummary. */
  private showObjectivesBriefing(
    side: number | undefined,
    fForceInfoDisplay: boolean,
    fAborting: boolean,
    done: (() => void) | null,
  ): void {
    if (this.m_domShowObjectives !== undefined) {
      // Mirror the canvas ShowObjectives (objectives.ts:318): queue the briefing in
      // the SAME per-session FIFO story-modal sequencer the intro Ecoms use
      // (ecom.ts:100, keyed on shellEnv.mfrmm) so it shows in the canvas-faithful
      // order — i.e. AFTER the mission-start Ecoms, not jumping ahead of them — and
      // the SP pause is held (fPause=true) until the overlay is dismissed. `next()`
      // (after the caller's `done`) releases the hold + shows the next queued modal.
      EnqueueStoryModal(this.shellEnv, true, (next) => {
        // The form host's getVar (gamesession.ts makeFormMgrHost) is what the canvas
        // LabelControl SetText uses to expand {pvar}/{$ranktitle} — reuse it so the
        // DOM snapshot's texts/briefing/rank expand byte-identically.
        const snap = objectivesSnapshot(this.objectivesDeps(side), this.mfrmm.host.getVar);
        this.m_domShowObjectives!(snap, () => {
          if (done !== null) done();
          next();
        });
      });
      return;
    }
    ShowObjectives(
      this.shellEnv,
      this.objectivesDeps(side),
      ksoObjectives,
      fForceInfoDisplay,
      fAborting,
      done === null ? null : () => done(),
    );
  }

  /** Automation seam (wiUi.openObjectives): trigger the in-game OBJECTIVES
   *  briefing the SAME way the credits-label tap does (InputUI.cpp:258-264 /
   *  inputui.ts:322-330) — SP pause held across the modal, resumed on close —
   *  WITHOUT driving the canvas pause menu. Routes through showObjectivesBriefing
   *  so the DOM overlay shows. No-op while the briefing is already up (the modal is
   *  single-instance). */
  private m_objectivesUp = false;
  openObjectivesForAutomation(): void {
    if (this.m_objectivesUp || this.fGameOver) return;
    this.m_objectivesUp = true;
    if (!this.sim.ctx.fMultiplayer) this.sim.sim.Pause(true);
    this.showObjectivesBriefing(undefined, false, false, () => {
      this.m_objectivesUp = false;
      if (!this.sim.ctx.fMultiplayer) this.sim.sim.Pause(false);
    });
  }

  /** Automation read: is the OBJECTIVES briefing currently up? */
  objectivesUp(): boolean {
    return this.m_objectivesUp;
  }

  /** The HUD attack-move toggle: arm/disarm attack-move on the SimUI (the next ground tap
   *  issues an attack-move and disarms). Returns the new armed state. (The HUD button plays
   *  its own tap sfx on pointerdown.) */
  toggleAttackMove(): boolean {
    return this.sui.ToggleAttackMoveArmed();
  }
  /** Automation read: is attack-move currently armed? */
  attackMoveArmed(): boolean {
    return this.sui.IsAttackMoveArmed();
  }

  /** ?dom HUD (Phase 5, Form 5): the power supplied per Reactor (apuntc[kutReactor]
   *  .nPowerSupply) — the units-per-pip the canvas PowerControl uses (inputui.ts:630)
   *  for the power meter. Constant per level; the DOM HudView reads it once. */
  powerSupplyPerPip(): number {
    return (this.sui.ctx.unitConsts[kutReactor] as unknown as StructConsts).nPowerSupply;
  }

  /** ?dom HUD (Phase 5, Form 5): the max per-structure power demand — the threshold
   *  for the YELLOW "warning" pip zone (one more structure could overload). (Phase 3
   *  — forms.ini elimination): the canvas PowerControl is GONE, so read the CANONICAL
   *  computeDemandMax directly (the same value the InputUILayer's per-frame DOM push
   *  caches and the old control computed in its Init). Constant per level. */
  powerDemandMax(): number {
    return computeDemandMax(this.sui.ctx.unitConsts);
  }

  /** ?dom HUD (Phase 5, Form 5): the kifntHud bitmap font (game.ini [Fonts] 5) the
   *  canvas CreditsControl/PowerControl draw their framed-box readouts through (its
   *  glyphs A–L are ICONS/frame-pieces, not letters). The DOM HUD renders the SAME
   *  glyph strings into a bitmap and shows it as an image so it's pixel-faithful to
   *  the canvas (it cannot be approximated in CSS). */
  hudFont(): Font {
    return this.renderer.fonts[kifntHud];
  }

  /** ?dom HUD (Phase 5, Form 5): the canvas console (input) strip height in GAME
   *  pixels — menuButton.cy + 2*4 margin (game.cpp:1316-1392, renderer.cyInput). The
   *  DOM bar mirrors it × the canvas magnification (kGameScale) so the bar + its
   *  readouts land at the SAME CSS size as the canvas HUD. */
  consoleStripHeight(): number {
    return this.renderer.cyInput;
  }

  /** the minimap pixel-per-tile SCALE (1 or 2; Phase 2 — forms.ini elimination),
   *  computed VERBATIM from the canvas MiniMapControl::Init (SimUI.cpp:2640-2654) off the
   *  playfield size. Also homes it on gctx.nMiniMapScale exactly as the C++ Init did
   *  (gsim.SetMiniMapScale) so any minimap-layer reader sees the same value. */
  private minimapScale(): number {
    const siz = this.sui.GetPlayfieldSize();
    let nScale = 1;
    if (siz.cx >= 240 && siz.cx < 320) {
      if (siz.cy > siz.cx) nScale = 2;
    }
    if (siz.cx >= 320) nScale = 2;
    this.sui.gctx.nMiniMapScale = nScale;
    return nScale;
  }

  /** the per-frame MiniMapModel the DOM MiniMapView consumes (Phase 2 — forms.ini
   *  elimination) — the SAME clean sim/tilemap getters the canvas MiniMapControl
   *  read (tmap.m_pwMapData / m_miniTset / GetMiniTileSetHeader, ctx.fogm/trmap GetMapPtr,
   *  ctx.gobm.GetUnitGob / GetMapSize; coords; sui.GetViewPos/GetPlayfieldSize). The
   *  coalesced content-dirty state is consumed (read + cleared) here so the view
   *  re-renders the radar dib only when the sim mutated it — and only the dirty
   *  tile-rect when the mutations were tile-precise (dirtyRect null = full redraw).
   *
   *  The model object is CACHED per (map,scale) and its volatile fields refreshed in
   *  place: this runs on every rAF (up to 120Hz), and the old fresh-nested-object +
   *  three-closures-per-call shape was pure GC churn. */
  private mmModel: MiniMapModel | null = null;
  private mmModelScale = -1; // (map dims never change mid-session; scale can, on resize)
  private readonly mmWpt = { wx: 0, wy: 0 };
  /** the dirtyRect handed to the view — reused (consumed synchronously by update()) */
  private readonly mmDirtyRectOut = { left: 0, top: 0, right: 0, bottom: 0 };

  minimapModel(): MiniMapModel {
    const ctx = this.sui.ctx;
    const tmap = this.tmap;
    const coords = this.coords;
    const nScale = this.minimapScale();
    const dims = ctx.gobm.GetMapSize();
    if (this.mmModel === null || nScale !== this.mmModelScale) {
      const pmtseth = tmap.GetMiniTileSetHeader(nScale);
      const sizMap = tmap.GetMapSize();
      this.mmModel = {
        data: {
          ctx: dims.ctx,
          cty: dims.cty,
          nScale,
          tileMap: tmap.m_pwMapData!,
          miniTset: tmap.m_miniTset!,
          ibTileData: (pmtseth?.off ?? -8) + 8,
          fogMap: ctx.fogm!.GetMapPtr(),
          trMap: ctx.trmap!.GetMapPtr(),
          gobAt: (tx: number, ty: number): MiniMapGob | null =>
            ctx.gobm.GetUnitGob(tx, ty) as unknown as MiniMapGob | null,
        },
        surface: {
          sizMapCx: sizMap.cx,
          sizMapCy: sizMap.cy,
          gcxTile: coords.gcxTile,
          gcyTile: coords.gcyTile,
          cyInputUI: this.renderer.cyInput,
          fMultiplayer: ctx.fMultiplayer,
          sideLocal: ctx.plrLocal!.GetSide(),
        },
        view: {
          gcxTile: coords.gcxTile,
          gcyTile: coords.gcyTile,
          PcFromUwc: (wc: number) => coords.PcFromUwc(wc),
          WcFromUpc: (pc: number) => coords.WcFromUpc(pc),
          playfieldCx: 0,
          playfieldCy: 0,
          wxView: 0,
          wyView: 0,
        },
        input: {
          gcxTile: coords.gcxTile,
          gcyTile: coords.gcyTile,
          nScale,
          playfieldCx: 0,
          playfieldCy: 0,
          wxView: 0,
          wyView: 0,
        },
        dirty: false,
        dirtyRect: null,
      };
      this.mmModelScale = nScale;
      // a rebuilt model must repaint everything once
      this.minimapDirtyAllSet();
    }
    const m = this.mmModel;
    // live-buffer refs refreshed per call: a snapshot restore can swap the sim's
    // fog/terrain/tile buffers under a stable session
    m.data.fogMap = ctx.fogm!.GetMapPtr();
    m.data.trMap = ctx.trmap!.GetMapPtr();
    m.data.tileMap = tmap.m_pwMapData!;
    // volatile scalars (scroll + resize)
    const sizPf = this.sui.GetPlayfieldSize();
    const wpt = this.mmWpt;
    this.sui.GetViewPos(wpt);
    m.view.playfieldCx = m.input.playfieldCx = sizPf.cx;
    m.view.playfieldCy = m.input.playfieldCy = sizPf.cy;
    m.view.wxView = m.input.wxView = wpt.wx;
    m.view.wyView = m.input.wyView = wpt.wy;
    // consume the coalesced content-dirty state
    m.dirty = this.mmDirty !== 'none';
    if (this.mmDirty === 'rect') {
      const out = this.mmDirtyRectOut;
      const r = this.mmDirtyRect;
      out.left = r.left;
      out.top = r.top;
      out.right = r.right;
      out.bottom = r.bottom;
      m.dirtyRect = out;
    } else {
      m.dirtyRect = null;
    }
    this.mmDirty = 'none';
    return m;
  }

  /** the minimap click-to-jump / drag-scroll map-pin (Phase 2) — sui.SetViewPos,
   *  the SAME seam the canvas MiniMapControl::OnPenEvent2 drove. */
  minimapSetViewPos(wx: number, wy: number): void {
    this.sui.SetViewPos(wx, wy);
  }

  /** Sub-pixel residual carried between wheel events (the fractional game-px a
   *  given event didn't move). WcFromPc takes an int, so without this a slow
   *  trackpad scroll — whose per-event delta is often < 1 game px, especially at
   *  kGameScale 1.5 — would truncate to zero every event and never move (steppy). */
  private m_wheelResidX = 0;
  private m_wheelResidY = 0;

  /** Two-finger trackpad scroll (or mouse wheel) map pan — the wheel analogue of
   *  the FingerHandler's drag pan (fingerhandler.cpp:658-663 / UpdateDrag). dxPx/dyPx
   *  are game-pixel deltas (browser wheel deltaX/deltaY, positive-down), so the view
   *  moves WITH them — the gesture scrolls the map like a scrollbar. Scaled by
   *  gnScrollSpeed and pinned by SetViewPos, exactly as the drag pan is. */
  panViewByPixels(dxPx: number, dyPx: number): void {
    const s = this.sui.gnScrollSpeed;
    this.m_wheelResidX += dxPx * s;
    this.m_wheelResidY += dyPx * s;
    // Apply only the whole-pixel part; carry the fraction to the next event.
    const px = Math.trunc(this.m_wheelResidX);
    const py = Math.trunc(this.m_wheelResidY);
    if (px === 0 && py === 0) return;
    this.m_wheelResidX -= px;
    this.m_wheelResidY -= py;

    const wpt: WPoint = { wx: 0, wy: 0 };
    this.sui.GetViewPos(wpt);
    const c = this.sui.gctx.coords;
    // WcFromPc asserts |pc| <= kpcMax; clamp the (rare) large mouse-wheel step.
    const clamp = (n: number): number => (n > kpcMax ? kpcMax : n < -kpcMax ? -kpcMax : n);
    this.sui.SetViewPos(wpt.wx + c.WcFromPc(clamp(px)), wpt.wy + c.WcFromPc(clamp(py)));
  }

  /** the kifntShadow line height in GAME px (game.ini [Fonts] 1) — the font the canvas
   *  SimUIForm drew its 4 overlay text labels through. The DOM SimUIView sizes its web
   *  text to this × the magnification so the line height matches the canvas labels.
   *  Read off the same renderer font table hudFont() uses. */
  simUiFontHeight(): number {
    return this.renderer.fonts[kifntShadow].GetHeight();
  }

  /** "is a DOM full-screen story overlay (the in-mission CUTSCENE) still up?"
   *  WireJumpToMission's DOM guard reads this — the DOM analogue of GetModalForm()
   *  (the DOM cutscene never lands on the canvas form stack). false in a headless
   *  harness (the predicate is unset). */
  domStoryOverlayActive(): boolean {
    return this.m_domStoryOverlayActive?.() ?? false;
  }

  /** Automation seam (wiUi.openPauseMenu): open the in-game PAUSE MENU the SAME
   *  way the HUD MENU button does (inputDeps.inGameMenu → DoInGameMenu, which
   *  mounts the DOM PauseMenuView and holds the SP pause). No-op while
   *  the game is over. The pause/un-pause + actions are DoInGameMenu's (the shared
   *  handler) — this just presses the button. */
  openInGameMenuForAutomation(): void {
    if (this.fGameOver) return;
    DoInGameMenu(this.shellEnv);
  }

  /** The ggame/gsim/gplrm/gpcptm reaches of Player::ShowObjectives
   *  (ui/objectives.ts) for the player on `side` (default: gpplrLocal). */
  objectivesDeps(side?: number): ObjectivesDeps {
    const plr = side === undefined ? this.sim.ctx.plrLocal : this.sim.ctx.plrm.GetPlayer(side);
    return {
      plr: plr as unknown as ObjectivesPlayer,
      getLevelTitle: () => this.levelTitle(),
      simTickCount: () => this.sim.sim.GetTickCount(),
      // gplrm.SendWinStats() — Player.cpp:361-363 returns unless
      // gfMultiplayer && gptra; the guard lives inside SendWinStats
      sendWinStats: () => {
        this.SendWinStats();
      },
      markComplete:
        this.completeMgr === undefined
          ? undefined
          : () => {
              this.completeMgr!.MarkComplete(this.miid());
            },
      // ggame.SkipSaveReinitializeGame (Player.cpp:1224 — the app-exit arm)
      skipSaveReinitializeGame: () => {
        this.SkipSaveReinitializeGame();
      },
    };
  }

  // game/game.cpp:1942-1964 Game::SaveReinitializeGame — the web resume slot
  // (knGameReinitializeSave = -3): skipped after SkipSaveReinitializeGame, in
  // multiplayer, and on hosts without a SaveStore. SaveGame closes the stream.
  SaveReinitializeGame(): boolean {
    if (this.fSkipSaveReinitialize) return false;
    if ((this.wfRole & kfRoleMultiplayer) !== 0) return false; // gfMultiplayer
    if (this.saveStore === undefined) return false;
    const pstm = HostNewSaveGameStream(this.saveStore, knGameReinitializeSave, 'Reinitialize');
    if (pstm === null) return false;
    SaveGame(
      {
        miid: this.miid(),
        szNextLevel: this.szNextLevel,
        saveState: () => this.sim.saveState(),
      },
      pstm,
    );
    return true;
  }

  // game/game.cpp:1966-1976 Game::DeleteReinitializeGame — open-with-delete
  // drops the slot (the #if defined(WEB) arm; io/savegame.ts removes on open)
  DeleteReinitializeGame(): void {
    if (this.saveStore === undefined) return;
    const pstm = HostOpenSaveGameStream(this.saveStore, knGameReinitializeSave, true);
    pstm?.Close();
  }

  // ht.h:2425 Game::SkipSaveReinitializeGame — the objectives form's app-exit
  // arm (Player.cpp:1224) latches it so the summary isn't re-entered on resume
  SkipSaveReinitializeGame(): void {
    this.fSkipSaveReinitialize = true;
  }

  // game/game.cpp:2554-2559 — Game::SetGameSpeed: move the global and retime
  // the SimUIForm's update timer. The C++ retimes with gtGameSpeed directly (an
  // integer rate). F11 retimes with m_ctTimerRate, which EQUALS gtGameSpeed for
  // every integer speed (bit-identical) and is the integer carrier rate (1 tick)
  // for the SP 11x notch — the fractional pacing happens in the OnTimer SP arm.
  SetGameSpeed(t: number): void {
    this.gtGameSpeed = t;
    this.ComputeSpeedParams(t);
    this.gtimm.SetTimerRate(this.simTimer, this.m_ctTimerRate);
  }

  // Derive the accumulator params (m_speedNum/m_speedDen/m_ctTimerRate) from a
  // game speed and reset the residual carry. For an INTEGER speed t (every C++
  // gatGameSpeeds entry): the timer rate stays t and exactly one update fires
  // per tick-block — speedNum/speedDen = 1/t makes `ctTimerRate*num/den` = 1.0,
  // floor = 1, residual 0. For the SP-only fractional 11x notch (kt11x = 8/11):
  // the timer fires every tick (rate 1) and the accumulator delivers 11 updates
  // per 8 ticks (137.5 ups). The fractional notch is SP-ONLY — an MP session's
  // gtGameSpeed is always an integer wire field (validated server-side).
  private ComputeSpeedParams(t: number): void {
    this.m_accNum = 0;
    if (t >= 1) {
      // every integer game speed (the C++ behavior, verbatim): one update/fire
      const ti = Math.trunc(t);
      this.m_speedNum = 1;
      this.m_speedDen = ti;
      this.m_ctTimerRate = ti;
    } else {
      // the SP 11x notch (kt11x = 8/11): updates-per-tick = 1/t = 11/8
      this.m_speedNum = 11;
      this.m_speedDen = 8;
      this.m_ctTimerRate = 1; // fire every tick; the accumulator paces the rest
    }
  }

  updateCount(): number {
    return this.sim.updateCount();
  }

  // game/game.cpp:2082-2119 Game::SizeAndPositionEverything — moved to the
  // render stack (it only touches render fields); GameSession delegates.
  SizeAndPositionEverything(): void {
    this.renderer.SizeAndPositionEverything();
  }

  // The display resize entry (game/sdl/host.cpp:57-71 HostPollPendingResize) —
  // moved to the render stack (it recreates the back dib + re-lays the form
  // managers/tile map, all render fields); GameSession delegates.
  resize(cx: number, cy: number): void {
    this.renderer.resize(cx, cy);
  }

  /** The shared back dib's rgba — current as of the last DrawFrame. */
  frame(): Uint8Array {
    return this.renderer.frame();
  }

  /** Push a host pen event (HostGetEvent's queue); the EventMgr cooks it
   *  (gpmfrmm->CookEvent) and the pump routes it to the forms. */
  injectPen(eType: number, x: number, y: number, ff = 0): void {
    const evt = newEvent(eType);
    evt.x = x;
    evt.y = y;
    evt.ff = ff; // kfEvtRightButton rides in from the host (host.cpp:358-385)
    evt.ms = this.ticks * 10;
    this.inputQ.push(evt);
  }

  /** Advance the scripted clock n ticks, one tick at a time; after each step
   *  pump the event loop dry — game.cpp:1801-1844's
   *  `GetEvent / FilterEvent / DispatchEvent` passes, non-blocking. While a
   *  form is modal the SAME pump is form.cpp:578-622's nested DoModal loop
   *  (the C++ blocks inside DoModal; here the loop dissolved into this pump —
   *  see Form.DoModal): the appStop/gameOver/cancelMode force-end arms, the
   *  modal form's OnFilterEvent, and the loop-exit epilogues all run here. */
  advanceTicks(n: number): void {
    // game.cpp:1825-1851 — gameOverEvent BREAKS the loop (ExitSimulation
    // follows); a finished session is terminal, nothing pumps after.
    if (this.fGameOver) return;
    // Cosmetic projectile interpolation: the in-game pump paints ON DEMAND (≈ sim rate,
    // ~12.5/sec), so the interp's in-between positions would never be painted. While
    // projectiles are airborne (and not paused) request MAX-rate repaints (kfRedrawMax,
    // FPS-gated by gcmsDisplayUpdate) so the glide actually displays; the dirty-rect pass still
    // limits each repaint to the projectiles. Cleared when none remain (or paused) → back
    // to efficient on-demand painting. Render-only; no sim effect.
    // The governor caps the PAINT PACE (gcmsDisplayUpdate) to ~30 FPS when the device is behind on
    // displayed-frame cadence (issue #11) — this drain loop paints once per allowed interval, so a
    // coarser pace drops the wasted sub-frame repaints (only the last is presented) that peg a weak
    // GPU or a heavy scene. A device that keeps up stays at the default ~125 FPS cap (full glide).
    if (hasActiveProjectileInterp() && !this.sim.sim.IsPaused()) {
      this.evm.SetRedrawFlags(kfRedrawMax);
      this.evm.gcmsDisplayUpdate = glidePaintIntervalMs();
    } else {
      this.evm.ClearRedrawFlags(kfRedrawMax);
      this.evm.gcmsDisplayUpdate = kDisplayUpdateDefault;
    }
    for (let i = 0; i < n; i++) {
      // Coalesce a multi-tick catch-up drain to ONE paint: CheckPaintFPS paces on
      // the sim clock (ticks*10), so without this every drained tick composites a
      // full frame while the embedder presents once per rAF — n-1 frames of work
      // discarded unseen, and the deeper a client falls behind the more each rAF
      // costs (the combat death-spiral). Non-final ticks pass fSuppressPaint to
      // GetEvent below (timers/input still dispatch); the final tick paints with
      // every drained update included. Render-only.
      const fSuppressPaint = i < n - 1;
      this.ticks++;
      // game.cpp:1834-1841 — "Continuously save on web because there is no
      // Exit": refresh the resume slot every 500ms of host clock
      const msCurrent = this.ticks * 10;
      if (msCurrent - this.msLastReinitSave > 500) {
        this.msLastReinitSave = msCurrent;
        this.SaveReinitializeGame();
      }
      // a form may have been EndForm'd between ticks (embedder calls) — its
      // DoModal epilogue is pump-side, run it before pumping
      this.DrainModalExits();
      // game.cpp:1805 `gevm.GetEvent(&evt, -1, false)` — the in-game loop runs
      // with fCheckPaints=false (paints ride explicit SetRedrawFlags), but the
      // modal loop's PeekEvent (form.cpp:589 `gevm.PeekEvent(&evt, -1)`) keeps
      // the fCheckPaints=true default. The browser-shaped pump passes ctWait=0
      // (one non-blocking pass per call) and keeps going until the EventMgr
      // runs dry for this tick.
      const evt = newEvent();
      while (this.evm.GetEvent(evt, 0, this.modalStack.length !== 0, fSuppressPaint)) {
        // form.cpp:595-603 — appStop/gameOver/cancelMode force-end EVERY modal
        // loop: each nested C++ DoModal peeks the same still-queued event,
        // takes m_idcDefault and unwinds (innermost first), THEN the outer
        // game loop processes the event itself.
        if (
          evt.eType === appStopEvent ||
          evt.eType === gameOverEvent ||
          evt.eType === cancelModeEvent
        ) {
          while (this.modalStack.length !== 0) {
            const top = this.modalStack[this.modalStack.length - 1];
            top.pfrm.m_idcLast = top.pfrm.m_idcDefault; // form.cpp:599
            top.pfrm.EndForm(top.pfrm.m_idcDefault); // form.cpp:600-601
            this.DrainModalExits();
          }
        }
        // game.cpp:1813-1832 — loop-exit events stop the session's pumping
        if (evt.eType === appStopEvent) {
          // game.cpp:1819-1820 — save the resume slot unless the host says
          // the app is exiting for real (knAppExit, ht.h:1866)
          if (evt.dw !== knAppExit) this.SaveReinitializeGame();
          return;
        }
        if (evt.eType === gameOverEvent) {
          this.fGameOver = true;
          this.nGo = evt.dw; // game.cpp:1828 nGo = (int)evt.dw
          this.DeleteReinitializeGame(); // #if defined(WEB), game.cpp:1827-1829
          return;
        }
        // form.cpp:605-610 — auto-takedown (Form::OnHitTest inside CookEvent,
        // which ran inside GetEvent) may have ended a modal form; its DoModal
        // epilogue runs BEFORE the event dispatches (the C++ leaves the event
        // peeked, exits the loop, and the outer context re-gets it).
        this.DrainModalExits();
        if (this.FilterEvent(evt)) continue;
        // form.cpp:619-620 — the innermost modal form's own filter
        const top = this.modalStack[this.modalStack.length - 1];
        if (top !== undefined && top.pfrm.OnFilterEvent(evt)) continue;
        this.DispatchEvent(evt);
        // EndForm during dispatch exits the C++ loop (the form.cpp:578 while
        // condition) — run the epilogue now
        this.DrainModalExits();
      }
    }
  }

  // game/event.cpp:467-494 — gevm.DispatchEvent
  private DispatchEvent(pevt: Event): boolean {
    // event.cpp:469-486 (WI_AUTOMATION) — synthetic control activation: route to
    // the current modal form; during live gameplay with no modal form up, the only
    // in-game canvas control was the InputUI MENU button. (Phase 3 — forms.ini
    // elimination): the InputUI bar is a control-less PaintLayer now, so route the
    // MENU-button press straight to its in-game-menu handler (the DOM HUD owns the
    // real button; this keeps the wiAuto press(kidcMenuButton) automation seam live).
    if (pevt.eType === wiAutoSelectEvent) {
      // GetModalForm() is always null now (no forms.ini Form is ever modal), so
      // this routes straight to the InputUI in-game-menu arm; kept for shape (a
      // modal layer, if one ever set kfFrmDoModal, would satisfy ModalForm).
      const pfrm = this.mfrmm.GetModalForm() as unknown as ModalForm | null;
      if (pfrm !== null) {
        pfrm.WiAutoSetLastControl(pevt.x & 0xffff);
        pfrm.OnControlSelected(pevt.x & 0xffff);
      } else if ((pevt.x & 0xffff) === kidcMenuButton) {
        this.pfrmInputUI.InGameMenu(); // ggame.GetSimUIForm() != NULL here
      }
      return true;
    }
    // route by the cooked form id; unclaimed events (idf 0) fall on the floor
    const pfrm = this.mfrmm.GetFormPtr(pevt.idf);
    if (pfrm !== null) return pfrm.EventProc(pevt);
    return false;
  }

  // form.cpp:578 + 624-632 — pop every modal loop whose kfFrmDoModal has
  // cleared and run its epilogue, innermost first. Stops at the first
  // still-modal entry: an OUTER form ended early keeps its C++ DoModal frame
  // blocked under the inner loop until that one exits too.
  private DrainModalExits(): void {
    while (this.modalStack.length !== 0) {
      const top = this.modalStack[this.modalStack.length - 1];
      if (top.pfrm.GetFlags() & kfFrmDoModal) break;
      this.modalStack.pop();
      top.onLoopExit();
    }
  }

  /** The wiAuto bridge's synthetic control press (ht.h:1853 wiAutoSelectEvent;
   *  event.cpp:469-486): posted into gevm with Event::x = the control id; the
   *  pump's DispatchEvent routes it to the modal form (or the input overlay). */
  pressControl(idc: number): void {
    const evt = newEvent(wiAutoSelectEvent);
    evt.x = idc;
    this.evm.PostEvent(evt, false);
  }

  // Game::FilterEvent (game.cpp:2271-2333) — the live-session arms, SP + MP.
  // (transportEvent is the C++ socket pump — the JS event loop owns it here;
  // gameSuspendEvent is the OS-alert platform arm.)
  private FilterEvent(evt: Event): boolean {
    // game.cpp:2278 UpdateTriggers() — the trigger pump services at the top of
    // EVERY FilterEvent pass, i.e. effectively right after the update that
    // scheduled it (events ALWAYS flow then, input or not: the update POSTS
    // updateTriggersEvent and posted events return from GetEvent before
    // HostGetEvent — see header note 2). pumpPolicy 'starved' is diagnostic.
    if (this.pumpPolicy === 'serviced') this.sim.sim.RunPendingTriggerUpdate();

    switch (evt.eType) {
      case checkGameOverEvent:
        // game.cpp:2280-2284 — m_pfrmSimUI->CheckMultiplayerGameOver(pevt->dw)
        this.CheckMultiplayerGameOver(evt.dw);
        break;

      case gamePaintEvent:
        // game.cpp:2285-2295 — gcPaint++ (the FPS stat) then the frame draw
        this.pfrmSimUI.gcPaint++;
        this.mfrmm.DrawFrame(true);
        return true;

      case mpEndMissionWinEvent:
        // game.cpp:2309-2312 — EndMissionAction::OnMPEndMissionActionEvent
        this.OnMPEndMissionActionEvent(true, evt.dw);
        break;

      case mpEndMissionLoseEvent:
        // game.cpp:2314-2317
        this.OnMPEndMissionActionEvent(false, evt.dw);
        break;

      case mpShowObjectivesEvent:
        // game.cpp:2319-2321 — ShowObjectivesAction::OnMPShowObjectivesEvent
        // (TriggerActions.cpp:1159-1162): don't pause the simulation
        ShowObjectives(
          this.shellEnv,
          this.objectivesDeps(evt.dw),
          ksoObjectives,
          true,
          false,
          null,
        );
        break;

      case disableSoundEvent:
        this.sndm.SaveStateAndClear(); // game.cpp:2322-2324
        break;

      case enableSoundEvent:
        this.sndm.RestoreState(); // game.cpp:2326-2328
        break;
    }
    return false;
  }

  // game/SimUI.cpp:768-871 — OnTimer: the SP slice plus the kfRoleMultiplayer
  // CLIENT arms (the local-lag alert, the flush-commands-to-server block,
  // ProcessUpdateMessage through the proven SimDriver). The WI_AUTOMATION
  // pause-point block is harness-only; CheckLagForm (the kill-lagging-player
  // vote form, SimUI.cpp:1154-1171) is an M5b seam.
  private OnTimer(_tCurrent: number): void {
    // SimUI.cpp:769-772 (TRACKSTATE) — after a sync error, do nothing
    if (this.m_fSyncError) return;

    // SimUI.cpp:796-799 — the pause/suspend gate still keeps the screen fresh
    if (this.fSuspendUpdates && !this.fSingleStep) {
      this.evm.SetRedrawFlags(kfRedrawDirty);
      return;
    }
    this.fSingleStep = false; // SimUI.cpp:800
    if (this.sim.sim.IsPaused()) {
      // SimUI.cpp:801-803
      return;
    }

    const fMpClient = (this.wfRole & (kfRoleMultiplayer | kfRoleServer)) === kfRoleMultiplayer;

    if (fMpClient) {
      // SimUI.cpp:805-819 — the "Network lag" alert: no server traffic for kctLocalLag ticks while the
      // client is live. While we're mid-reconnect the gap is expected, so show "Reconnecting…" instead.
      if (this.m_fReconnecting) {
        ShowAlert(this.pfrmSimUI.alertState, 'Reconnecting…');
        this.evm.SetRedrawFlags(kfRedrawDirty);
      } else if (this.m_tLastCommunication !== 0) {
        if (this.ticks - this.m_tLastCommunication >= kctLocalLag) {
          ShowAlert(this.pfrmSimUI.alertState, 'Network lag');
          // When the client is waiting on the server it won't redraw unless asked.
          this.evm.SetRedrawFlags(kfRedrawDirty);
        }
      }

      // SimUI.cpp:821-839 — send queued commands to the server whenever there
      // are any (the ClientCommandsNetMessage image of gcmdq), then Clear
      const cmdq = this.sim.ctx.cmdq;
      const cmsg = cmdq.GetCount();
      if (cmsg !== 0) {
        this.mpTransport!.SendNetMessage({
          nmid: NMID.CsClientCommands,
          kind: 'clientcommands',
          commands: cmdq.GetFirst().slice(0, cmsg).map(encodeMessage),
        });
      }
      cmdq.Clear();

      // SimUI.cpp:841-850 — process the next update message; the SimDriver IS
      // ProcessUpdateMessage + BeginTrackState/Update/EndTrackState
      // (sim/src/sim/driver.ts StepOnce; its trigger pump stays suppressed —
      // FilterEvent/RunUpdatesNow own the pump in the live client).
      const driver = this.sim.driver;
      // SimUI.cpp:676-681 — at the block point for the first time? stamp the
      // ms for the latency calc
      if (
        this.sim.sim.GetUpdateCount() + 1 === driver.m_cUpdatesBlock &&
        this.m_msUpdatesBlock === 0
      ) {
        this.m_msUpdatesBlock = this.ticks * 10;
      }
      const qBefore = driver.cQueued;
      driver.StepOnce(); // false = BLOCK (SimUI.cpp:688 "Trace BLOCK!")
      if (driver.cQueued < qBefore) {
        // a message was dequeued (its hash report, if due, already fired via
        // onBlockHash with msReceivedQ[0]) — retire the receipt stamp and the
        // block-point stamp (SimUI.cpp:735-744)
        this.msReceivedQ.shift();
        this.m_msUpdatesBlock = 0;
      }
    } else {
      // SimUI.cpp:852-856 — Update (i.e., 'step') the Simulation:
      // `gsim.Update(&gcmdq)` — the sim CONSUMES the local queue directly
      // (see header note 1; locally-enqueued commands execute at this update).
      //
      // F11 fractional pacing (SP-ONLY): the timer fired for m_ctTimerRate host
      // ticks; the integer-rational accumulator decides how many sim updates
      // those ticks owe. For EVERY integer game speed this is exactly ONE update
      // (m_ctTimerRate*1/t = 1.0, residual 0) — bit-identical to the pre-F11
      // single Update call. For the SP 11x notch it is 1 or 2 (avg 1.375).
      this.m_accNum += this.m_ctTimerRate * this.m_speedNum;
      const nUpdates = Math.trunc(this.m_accNum / this.m_speedDen);
      this.m_accNum -= nUpdates * this.m_speedDen;
      for (let i = 0; i < nUpdates; i++) {
        if (this.sim.sim.IsPaused()) break; // a trigger pump may have paused us
        this.sim.sim.Update(this.sim.ctx.cmdq);
        // Service the trigger pump BETWEEN updates exactly as RunUpdatesNow does
        // (the MP catch-up, SimUI.cpp:647/655) — Game::UpdateTriggers runs from
        // the event loop between fires, so a batch of fires in one tick must too.
        // NOT after the last update: a single fire leaves the pending pump for
        // the next FilterEvent pass (the pre-F11 path), so the degenerate 1-update
        // batch makes no extra pump call — bit-identical.
        if (i + 1 < nUpdates && this.pumpPolicy === 'serviced') {
          this.sim.sim.RunPendingTriggerUpdate();
        }
      }
    }

    // SimUI.cpp:1195-1198 (inside Update) — the first update takes down the
    // "WAITING FOR ALL PLAYERS..." overlay (the ?dom WaitingView).
    if (this.m_domWaitingUnmount !== null) {
      this.m_domWaitingUnmount();
      this.m_domWaitingUnmount = null;
    }

    // SimUI.cpp:858-861 — Update UI (the HUD refresh; raises kfRedrawDirty)
    this.pfrmSimUI.Update();

    // SimUI.cpp:868-870 — check to see if something needs to be done with the
    // lag form (the kill-lagging-player vote reply)
    this.CheckLagForm();
  }

  // ── the IGameCallback slice (game/SimUI.cpp MP arms) ────────────────────────

  /** game/SimUI.cpp:410-470 SimUIForm::OnNetMessage — the in-game NetMessage
   *  dispatch. The embedder wires the transport's game callback here
   *  (gptra->SetGameCallback(this), SimUI.cpp:278). */
  OnNetMessage(nm: GameInner): void {
    // TRACKSTATE: after a sync error, do not respond to any messages
    if (this.m_fSyncError) return;

    // SimUI.cpp:423-425 — track the last time communication was received
    this.m_tLastCommunication = this.ticks;

    switch (nm.nmid) {
      case NMID.ScPlayerDisconnect: {
        // SimUI.cpp:428-443 — over the wire this carries {pid, nReason} words;
        // the in-sim route (host.OnPlayerDisconnect) calls the notify directly
        if (nm.kind === 'rawinner' && nm.bytes.length >= 4) {
          const dv = new DataView(nm.bytes.buffer, nm.bytes.byteOffset);
          this.OnPlayerDisconnectMessage(dv.getUint16(0), dv.getUint16(2));
        }
        break;
      }

      case NMID.ScLagNotify:
        // SimUI.cpp:444-450 — this player is being notified by the server of
        // another player that is lagging
        if (nm.kind === 'lagnotify') {
          this.OnLagNotify(nm.pidLagging, nm.cSeconds);
        }
        break;

      case NMID.ScUpdate:
        if (nm.kind === 'scupdate') this.QueueUpdateMessage(nm);
        break;

      case NMID.ScCheckWin:
        // SimUI.cpp:459-461 — CheckWinNetMessage carries the claimed winner's
        // pid (a BE word; no typed decode — it rides rawinner)
        if (nm.kind === 'rawinner' && nm.bytes.length >= 2) {
          this.OnCheckWin((nm.bytes[0] << 8) | nm.bytes[1]);
        }
        break;

      case NMID.ScSyncError:
        // SimUI.cpp:464-466 (TRACKSTATE)
        this.ReportSyncError();
        break;

      case NMID.ScReplayAvailable:
        // TS-only: the server finalized this won game's recording to R2 and sent its archive key (UTF-8
        // body). Hand it to the host so the end-of-game summary's "VIEW REPLAY" lights up. No C++ analog.
        if (nm.kind === 'rawinner' && nm.bytes.length > 0) {
          this.m_onReplayAvailable?.(new TextDecoder().decode(nm.bytes));
        }
        break;
    }
  }

  /** game/SimUI.cpp:471-476 SimUIForm::OnReceiveChat — in-game chat lands as
   *  an alert + the chatter overlay. */
  OnReceiveChat(player: string, chat: string): void {
    ShowAlert(this.pfrmSimUI.alertState, `${player}: ${chat}`);
    this.m_pchatter?.AddChat(player, chat, false);
  }

  /** game/SimUI.cpp:490-514 — the ITransportCallback slice (the embedder wires
   *  gptra->SetCallback to this in-game, SimUI.cpp:277). The posted events ride
   *  idf = the SimUI form, exactly like the C++ — and exactly like the C++
   *  they are only CONSUMED while SimUIForm itself runs a modal loop (it never
   *  does): the in-game connection-close/server-message events fall through
   *  DispatchEvent → SimUIForm::EventProc, which ignores them. The lockstep's
   *  practical drop path is GAMEKILLED → OnGameDisconnect. */
  OnStatusUpdate(_pszStatus: string): void {}
  OnConnectionClose(): void {
    // TS-only reconnect: a mid-game socket drop normally ends the game. If the host wired a reconnect
    // hook and the game is still live, DON'T tear down — keep the session + sim + driver alive, show a
    // "Reconnecting…" alert, and let the host re-open the socket, re-login, and CsResumeGame. The DO holds
    // our slot meanwhile. (No hook / already over / sync-errored → the legacy inert event, as before.)
    if (
      this.m_onReconnectNeeded &&
      (this.wfRole & kfRoleMultiplayer) !== 0 &&
      !this.fGameOver &&
      !this.m_fSyncError
    ) {
      this.m_fReconnecting = true;
      ShowAlert(this.pfrmSimUI.alertState, 'Reconnecting…');
      this.evm.SetRedrawFlags(kfRedrawDirty);
      this.m_onReconnectNeeded();
      return;
    }
    const evt = newEvent(connectionCloseEvent);
    evt.idf = this.pfrmSimUI.m_idf;
    this.evm.PostEvent(evt);
  }

  /** Reconnect succeeded (host re-bound the slot): re-point the session at the fresh transport and resume
   *  normal play. The catch-up ScUpdate frames the DO replayed are already draining through the driver. */
  onReconnected(transport: MpTransport): void {
    this.mpTransport = transport;
    this.m_fReconnecting = false;
    this.m_tLastCommunication = this.ticks; // suppress an immediate stale "Network lag"
  }

  /** Reconnect gave up (grace expired / server rejected / network stayed down): fall back to the legacy
   *  disconnect → abort-to-menu teardown. */
  abortReconnect(): void {
    this.m_fReconnecting = false;
    this.OnGameDisconnect();
  }

  /** The driver's current lockstep block point — sent as CsResumeGame's lastBlock (the DO replays
   *  everything recorded after it). */
  resumeBlockPoint(): number {
    return this.sim.resumeBlockPoint();
  }
  OnShowMessage(message: string): void {
    this.s_message = message;
    const evt = newEvent(showMessageEvent);
    evt.idf = this.pfrmSimUI.m_idf;
    this.evm.PostEvent(evt);
  }
  /** SimUI.cpp:504 `std::string s_message`. */
  private s_message = '';

  /** game/SimUI.cpp:477-487 SimUIForm::OnGameDisconnect — the server dropped
   *  us (GAMEKILLED): message box then abort the level. */
  OnGameDisconnect(): void {
    this.shellEnv.msgBox(
      'Network',
      "You've been disconnected from this game. Press OK to continue.",
    );
    const evt = newEvent(gameOverEvent);
    evt.dw = knGoAbortLevel;
    this.evm.PostEvent(evt);
  }

  // game/SimUI.cpp:586-636 — queue the UpdateNetMessage (the driver expires
  // acked frames + tails it); if the client is blocked on this update — or got
  // BEHIND (2+ queued) — post runUpdatesNowEvent at the SimUI form so the
  // catch-up runs from the event loop ("running the simulation requires
  // running from the event loop").
  private QueueUpdateMessage(punm: {
    cUpdatesBlock: number;
    cUpdatesSync: number;
    commands: Uint8Array[];
  }): void {
    const driver = this.sim.driver;
    driver.QueueUpdateMessage({
      cUpdatesBlock: punm.cUpdatesBlock,
      cUpdatesSync: punm.cUpdatesSync,
      commands: punm.commands.map(decodeMessage),
    });
    this.msReceivedQ.push(this.ticks * 10); // punmr->msReceived (SimUI.cpp:593)

    let fUpdateNow = false;
    if (this.sim.sim.GetUpdateCount() + 1 === driver.m_cUpdatesBlock) {
      fUpdateNow = true;
    }
    if (driver.cQueued >= 2) {
      fUpdateNow = true;
    }
    if (fUpdateNow) {
      const evt = newEvent(runUpdatesNowEvent);
      evt.idf = this.pfrmSimUI.m_idf;
      this.evm.PostEvent(evt);
    }
  }

  // game/SimUI.cpp:636-661 SimUIForm::RunUpdatesNow — run a blocked-on update
  // immediately (timer re-phased), then drain any backlog; ggame.UpdateTriggers
  // services the pump after each (game.cpp:2256-2268).
  private RunUpdatesNow(): void {
    const driver = this.sim.driver;
    if (driver.cQueued > 0 && this.sim.sim.GetUpdateCount() + 1 === driver.m_cUpdatesBlock) {
      this.gtimm.RemoveTimer(this.simTimer);
      this.OnTimer(0);
      this.gtimm.AddTimer(this.simTimer, this.gtGameSpeed);
      if (this.pumpPolicy === 'serviced') this.sim.sim.RunPendingTriggerUpdate();
    }
    while (driver.cQueued >= 2) {
      const cBefore = driver.cQueued;
      this.gtimm.RemoveTimer(this.simTimer);
      this.OnTimer(0);
      this.gtimm.AddTimer(this.simTimer, this.gtGameSpeed);
      if (this.pumpPolicy === 'serviced') this.sim.sim.RunPendingTriggerUpdate();
      // The C++ loop relies on every pass consuming (pause/suspend can't hold
      // in MP); a no-progress pass here (paused/suspended/sync-error session)
      // must not spin the headless pump forever.
      if (driver.cQueued >= cBefore) break;
    }
  }

  // game/SimUI.cpp:754-766 SendUpdateResult — the per-block hash report
  // (UpdateResultNetMessage; the transport rides it on the 0x56 frame).
  private SendUpdateResult(cUpdatesBlock: number, cmsLatency: number, hash: number): void {
    this.mpTransport?.SendNetMessage({
      nmid: NMID.CsUpdateResult,
      kind: 'updateresult',
      cUpdatesBlock,
      hash: hash >>> 0,
      cmsLatency: cmsLatency | 0,
    });
  }

  // game/SimUI.cpp:535-585 (TRACKSTATE) ReportSyncError — latch m_fSyncError
  // (no state changes from here forward), stop the update timer, tell the
  // player, upload the tracker state, report analytics, end the game.
  ReportSyncError(): void {
    if (this.m_fSyncError) return;
    this.m_fSyncError = true;
    this.gtimm.RemoveTimer(this.simTimer);
    this.shellEnv.msgBox(
      'Sync Error',
      'This mission has experienced a sync error and must end. ' +
        'Please notify the mission author.',
    );

    // Upload the state tracker state (SimUI.cpp:568-572): POST the tracker's
    // ToJson to kszSyncErrorUploadUrl?gameid=&pid= (serviceurls.cpp:25) over
    // gphttp. `%d` renders the dword m_gameid SIGNED in the C++ url ⇒ `| 0`;
    // the wire body is the JSON text only — the ByteBuffer's trailing NUL
    // stops at the wget2 C-string boundary (see net/uploader.ts header).
    const pbb = new TextEncoder().encode(this.sim.ctx.tracker.ToJson());
    const url =
      `${kszSyncErrorUploadUrl}?gameid=${this.m_gameid | 0}` +
      `&pid=${(this.sim.ctx.plrLocal?.GetId() ?? 0) | 0}`;
    if (this.http === null) this.http = new FetchHttpService();
    UploadByteBuffer(this.http, pbb, url, {
      // Uploader::Upload's modal epilogue (uploader.cpp:63-65) — the error box
      msgBox: (title, msg) => {
        this.shellEnv.msgBox(title, msg);
      },
    });

    // Report analytics (SimUI.cpp:574-575)
    this.hostAnalytics(`/main/play/multi/${this.levelTitle()}/play/syncerror`);

    const evt = newEvent(gameOverEvent);
    evt.dw = knGoAbortLevel;
    this.evm.PostEvent(evt);
  }

  // game/SimUI.cpp:428-443 (the ScPlayerDisconnect arm) + :1020-1081
  // OnPlayerDisconnectNotify — runs at the same lockstep point on all clients
  // (it mutates player flags); then checkGameOverEvent is posted for the
  // asynchronous game-over scan (CheckMultiplayerGameOver = M5b).
  private OnPlayerDisconnectMessage(pid: number, nReason: number): void {
    if (this.m_fSyncError) return;
    this.m_tLastCommunication = this.ticks;

    const pplr = this.sim.ctx.plrm.GetPlayerFromPid(pid);
    if (pplr !== null) {
      pplr.SetFlags(pplr.GetFlags() | kfPlrComputer);
      // SimUI.cpp:1031-1037 — mark observers, but never the local player (the
      // flag is overloaded by the win/lose paths)
      if (pplr !== this.sim.ctx.plrLocal) {
        pplr.SetFlags(pplr.GetFlags() | kfPlrObserver);
      }
      // SimUI.cpp:1041-1078 — the alert text per reason
      let pszReason: string | null = null;
      switch (nReason) {
        case knDisconnectReasonLeftGame:
          if (!pplr.GetLeftGame()) {
            pplr.SetLeftGame(true);
            pszReason = 'left the game';
          }
          break;
        case knDisconnectReasonResign:
          pszReason = 'resigned';
          break;
        case knDisconnectReasonKilled:
          pszReason = 'kicked by another player';
          break;
        case knDisconnectReasonAbnormal:
          pszReason = 'dropped out';
          break;
        case knDisconnectReasonNotResponding:
          pszReason = 'disconnected - not responding';
          break;
      }
      if (pszReason !== null) {
        ShowAlert(this.pfrmSimUI.alertState, `${pplr.GetName()} ${pszReason}`);
      }
    }

    // SimUI.cpp:435-441 — asynchronously, check for game over
    const evt = newEvent(checkGameOverEvent);
    evt.dw = pid;
    this.evm.PostEvent(evt);
  }

  // ── the M5b MP arms: lag vote, game-over arbitration, observe/resign,
  //    win-stats upload, chat-players, teardown ───────────────────────────────

  // game/SimUI.cpp:1083-1152 SimUIForm::OnLagNotify — ack the server, alert,
  // and manage the MODELESS lag form (kfFrmDoModal set by hand, no nested
  // loop; CheckLagForm polls it each timer tick).
  private OnLagNotify(pidLagging: number, cSeconds: number): void {
    // First thing's first, acknowledge this to the server, so the server
    // knows this client is alive.
    this.mpTransport?.SendNetMessage({ nmid: NMID.CsLagAcknowledge, kind: 'empty' });

    // pidLagging is kpidNeutral if there are no laggards, which causes
    // GetPlayerFromPid() to return NULL.
    const pplr = this.sim.ctx.plrm.GetPlayerFromPid(pidLagging);
    if (pplr === null) {
      ShowAlert(this.pfrmSimUI.alertState, '');
      if (this.m_pfrmLag !== null) {
        this.m_pfrmLag.Show(false);
        this.m_pfrmLag.Dispose();
        this.m_pfrmLag = null;
      }
      return;
    }

    // We've been notified by the server of this player lagging. Display alert
    ShowAlert(this.pfrmSimUI.alertState, `${pplr.GetName()} lagging (${cSeconds}s)`);

    // If cSeconds is not 0, hide the form. It only comes up at 0.
    if (cSeconds !== 0) {
      if (this.m_pfrmLag !== null) {
        this.m_pfrmLag.Show(false);
        this.m_pfrmLag.Dispose();
        this.m_pfrmLag = null;
      }
      return;
    }

    // cSeconds is 0, then show the lag form. First see if it is for a
    // different player. If so, hide it first.
    if (this.m_pfrmLag !== null) {
      // If same pid is lagging, nothing to do; keep the form up
      if (this.m_pidLagging === pidLagging) return;
      this.m_pfrmLag.Show(false);
      this.m_pfrmLag.Dispose();
      this.m_pfrmLag = null;
    }

    // Show the lagging form, modeless, so that it can be hidden and reshown
    // at will. Check during update if in EndForm state, and handle
    // appropriately.
    this.m_pidLagging = pidLagging;
    this.m_pfrmLag = CreateLagForm(
      this.shellEnv,
      'LAG ALERT',
      `${pplr.GetName()} is communicating erratically and causing the game ` +
        `to lag. Disconnect ${pplr.GetName()}?`,
    );
    if (this.m_pfrmLag !== null) {
      this.m_pfrmLag.SetModal();
      this.sndm.PlaySfx(ksfxGuiFormShow);
      this.m_pfrmLag.Show(true);
    }
  }

  // game/SimUI.cpp:1154-1171 SimUIForm::CheckLagForm — when the modeless lag
  // form's kfFrmDoModal has cleared (a button press EndForm'd it), send the
  // CsKillLaggingPlayer vote.
  private CheckLagForm(): void {
    if (this.m_pfrmLag === null || this.m_pfrmLag.IsModal()) {
      return;
    }

    const nResult = this.m_pfrmLag.GetResult();
    this.m_pfrmLag.Show(false);
    this.m_pfrmLag.Dispose();
    this.m_pfrmLag = null;

    // Send a KillLaggingPlayerNetMessage with the result
    // (netmessage.h:452-457 {pid, fYes} — BE words on the wire)
    const bytes = new Uint8Array(4);
    const dv = new DataView(bytes.buffer);
    dv.setUint16(0, this.m_pidLagging & 0xffff, false);
    dv.setUint16(2, nResult === kidcOk ? 1 : 0, false);
    this.mpTransport?.SendNetMessage({
      nmid: NMID.CsKillLaggingPlayer,
      kind: 'rawinner',
      bytes,
    });
  }

  // game/SimUI.cpp:980-1018 SimUIForm::OnCheckWin — a client claimed a win
  // (challenge validation is the compiled-out #if 0 block); chat the claim.
  private OnCheckWin(pid: number): void {
    if (this.sim.ctx.plrLocal === null || this.mpTransport === null) return;

    // Assume this player won for purposes of chat system message
    const pplr = this.sim.ctx.plrm.GetPlayerFromPid(pid);
    if (pplr !== null && this.m_pchatter !== null) {
      this.m_pchatter.AddChat(null, `${pplr.GetName()} has won this game.`, true);
    }
  }

  // game/SimUI.cpp:921-969 SimUIForm::CheckMultiplayerGameOver — the
  // hamfistedly-hardwired transition-to-single-human-team scan (the C++
  // comment: should be triggers, isn't).
  CheckMultiplayerGameOver(pidLeft: number): void {
    if (!this.sim.ctx.fMultiplayer) return;

    if (this.sim.ctx.plrm.DetectTransitionToSingleHumanTeam(pidLeft)) {
      const plrLocal = this.sim.ctx.plrLocal!;

      // If this player isn't an observer it means they haven't officially
      // won/lost yet. Let them know the game is over and set them to be an
      // Observer.
      if ((plrLocal.GetFlags() & (kfPlrObserver | kfPlrSummaryShown)) === 0) {
        ShowObjectives(this.shellEnv, this.objectivesDeps(), ksoWinSummary, false, false, () => {
          this.AskObserveGame((fYes) => {
            if (!fYes) {
              const evt = newEvent(gameOverEvent);
              evt.dw = knGoAbortLevel;
              this.evm.PostEvent(evt);
            }
          });
        });
      } else {
        // Some other player is victorious. Show alert. There is only one
        // non-observer, non-computer player left.
        const pplr = this.sim.ctx.plrm.GetNextHumanPlayer(null);
        if (pplr !== null) {
          const szT = `${pplr.GetName()} is victorious!`;
          ShowAlert(this.pfrmSimUI.alertState, szT);
          this.m_pchatter?.AddChat('', szT, true);
        }
      }
    }
  }

  // game/TriggerActions.cpp:440-463 EndMissionAction::OnMPEndMissionActionEvent
  // — the local player's asynchronous mission end: modal summary, then the
  // observe question; observing posts cancelMode (modal forms fold), declining
  // aborts the level.
  private OnMPEndMissionActionEvent(fWin: boolean, side: number): void {
    const pplr = this.sim.ctx.plrm.GetPlayer(side);
    if (pplr !== this.sim.ctx.plrLocal) return;

    ShowObjectives(
      this.shellEnv,
      this.objectivesDeps(side),
      fWin ? ksoWinSummary : ksoLoseSummary,
      false,
      false,
      () => {
        // Single-human game: end the game (like SP) instead of offering to observe. EndMissionAction
        // already flagged the local player observer/computer (triggeractions.ts:255), so GetHumanTeamCount
        // now counts only the OTHER still-playing human teams; === 0 means nobody else is left to play
        // for — observing would just watch an AI-only match that can never reach the normal
        // single-human-team-collapse end (CheckMultiplayerGameOver / SimUI.cpp:921). The in-client "add
        // bot" feature made 1-human MP games common; the original C++ MP assumed >=2 human teams, so this
        // is a deliberate divergence. Multi-human games (>=1 other team) keep the observe flow unchanged.
        if (this.sim.ctx.plrm.GetHumanTeamCount(false, 0) === 0) {
          const evt = newEvent(gameOverEvent);
          evt.dw = fWin ? knGoSuccess : knGoFailure;
          this.evm.PostEvent(evt);
          return;
        }
        this.AskObserveGame((fYes) => {
          if (fYes) {
            // Cause modal forms to cancel such as build forms, help,
            // options, etc.
            this.evm.PostEventType(cancelModeEvent);
          } else {
            const evt = newEvent(gameOverEvent);
            evt.dw = knGoAbortLevel;
            this.evm.PostEvent(evt);
          }
        });
      },
    );
  }

  // game/game.cpp:1917-1940 Game::AskObserveGame — done(fYes) is the C++
  // return (the C++ blocks in HtMessageBox).
  AskObserveGame(done: (fYes: boolean) => void): void {
    const plrLocal = this.sim.ctx.plrLocal!;
    plrLocal.SetFlags(plrLocal.GetFlags() | kfPlrObserver);

    // psui->ClearSelection() (game.cpp:1923 -> SimUI.cpp:2030-2038)
    this.pfrmSimUI.ClearSelection();

    if (!this.evm.IsAppStopping()) {
      // the kfRoleServer host arm is the legacy serverless build; the lockstep
      // client always asks the plain question
      const psz =
        (this.wfRole & kfRoleServer) !== 0
          ? 'Do you want to continue as an observer? Since this device hosting, answering no will stop the game.'
          : 'Do you want to continue as an observer?';
      HtMessageBox(
        this.shellEnv,
        kfMbKeepTimersEnabled | kfMbWhiteBorder,
        'GAME OVER',
        psz,
        (fYes) => {
          if (fYes) this.SetObserving();
          done(fYes);
        },
        kidfMessageBoxQuery,
      );
      return;
    }

    done(false);
  }

  // game/game.cpp:1864-1915 Game::AskResignGame — done(fYes); fTellHost sends
  // CsPlayerResign. (The iPhone/Android app-stop fast-path is compiled out of
  // the web build; the ask always runs.)
  AskResignGame(fTellHost: boolean, done: (fYes: boolean) => void): void {
    // Ask user — kidsExitHost for the legacy serverless host, kidsExitClient
    // for the lockstep client
    const sz =
      this.strtbl.GetString((this.wfRole & kfRoleServer) !== 0 ? kidsExitHost : kidsExitClient) ??
      '';

    HtMessageBox(
      this.shellEnv,
      kfMbWhiteBorder | kfMbKeepTimersEnabled,
      'Warning',
      sz,
      (fYes) => {
        if (!fYes) {
          // If the user wants to keep going, return with "app stopping" cleared
          done(false);
          return;
        }

        if (fTellHost && this.mpTransport !== null) {
          // Tell host (PlayerResignNetMessage {pid} — BE word + struct pad)
          const bytes = new Uint8Array(4);
          new DataView(bytes.buffer).setUint16(
            0,
            (this.sim.ctx.plrLocal?.GetId() ?? 0) & 0xffff,
            false,
          );
          this.mpTransport.SendNetMessage({
            nmid: NMID.CsPlayerResign,
            kind: 'rawinner',
            bytes,
          });
        }
        done(true);
      },
      kidfMessageBoxQuery,
    );
  }

  // game/SimUI.cpp:971-978 SimUIForm::SetObserving
  SetObserving(): void {
    const plrLocal = this.sim.ctx.plrLocal!;
    plrLocal.SetObjective('< Observing >');
    this.sim.ctx.fogm?.RevealAll(); // gsim.GetLevel()->GetFogMap()->RevealAll(gpupdSim)
    // (Phase 3 — forms.ini elimination): no canvas SimUI control tree. The FPS
    // readout is a DOM SimUIView label (its visibility tracks fShowFPS), and the
    // observer chat button is a DOM-HUD concern — neither is a canvas control to
    // Show() anymore. The countdown visibility is canonical state (Phase 1): clear
    // the holder so the DOM SimUIView hides it, matching the C++ SetObserving.
    this.pfrmSimUI.countdownVisible = false;
    this.evm.SetRedrawFlags(kfRedrawDirty);
  }

  // game/gameform.cpp:204-297 GetPlayersString/GetPlayersPlayingString/
  // GetPlayersObservingString — the IN-GAME arm over the live sim roster
  // (gplrm/gpplrLocal); the staging-room arm lives on GameForm.
  GetPlayersString(): string {
    const playing = this.GetPlayersPlayingString();
    const observing = this.GetPlayersObservingString();
    if (observing.length === 0) return playing;
    return `${playing} ${observing}`;
  }

  private GetPlayersPlayingString(): string {
    const plrm = this.sim.ctx.plrm;
    const plrLocal = this.sim.ctx.plrLocal!;

    // Extract the player names from Player objects. Make this player player 0.
    const names: string[] = [];
    names.push(`${plrLocal.GetName()} (${GetColorName(plrLocal.GetSide())})`);
    for (
      let pplr = plrm.GetNextHumanPlayer(null);
      pplr !== null;
      pplr = plrm.GetNextHumanPlayer(pplr)
    ) {
      if (pplr.GetFlags() & kfPlrUnfulfilled) continue;
      if (pplr === plrLocal) continue;
      names.push(`${pplr.GetName()} (${GetColorName(pplr.GetSide())})`);
    }

    let s: string;
    if (plrLocal.GetFlags() & kfPlrObserver) {
      s = `You are ${GetColorName(plrLocal.GetSide())} and are observing. `;
    } else {
      s = `You are ${GetColorName(plrLocal.GetSide())}. `;
    }

    if (names.length === 1) {
      return s + 'No other players are in this game.';
    }
    if (names.length === 2) {
      return s + `${names[1]} is also in this game.`;
    }
    for (let i = 1; i < names.length; i++) {
      if (i !== names.length - 1) {
        s += `${names[i]}, `;
      } else {
        s += `and ${names[i]} `;
      }
    }
    return s + 'are also in this game.';
  }

  private GetPlayersObservingString(): string {
    const plrm = this.sim.ctx.plrm;
    const plrLocal = this.sim.ctx.plrLocal;
    const observers: string[] = [];
    for (
      let pplr = plrm.GetNextObservingPlayer(null);
      pplr !== null;
      pplr = plrm.GetNextObservingPlayer(pplr)
    ) {
      if (pplr.GetFlags() & kfPlrUnfulfilled) continue;
      if (pplr === plrLocal) continue;
      observers.push(`${pplr.GetName()} (${GetColorName(pplr.GetSide())})`);
    }

    if (observers.length === 0) return '';
    if (observers.length === 1) return `${observers[0]} is observing.`;

    let s = '';
    for (let i = 0; i < observers.length; i++) {
      if (i !== observers.length - 1) {
        s += `${observers[i]}, `;
      } else {
        s += `and ${observers[i]} `;
      }
    }
    return s + 'are observing.';
  }

  // game/Player.cpp:361-438 PlayerMgr::SendWinStats — collect per-player
  // stats, MD5-MAC them (over the NATIVE little-endian struct bytes:
  // ws ‖ subjectPid ‖ gameid ‖ localPid), XOR-obfuscate the struct with the
  // hash, and send one CsWinStats per player. The wire image (BE words after
  // the byteswap) mirrors protocol/gamemsg.ts decodeWinStats exactly.
  SendWinStats(): void {
    if (!this.sim.ctx.fMultiplayer || this.mpTransport === null) {
      return;
    }
    const plrm = this.sim.ctx.plrm;
    const plrLocal = this.sim.ctx.plrLocal;

    // Find the allies of the winner, if any. They are all winners. Since
    // kfPlrWinner only gets set by the human winner, this will only be != 0
    // by the winning player.
    let sidmWinners = 0;
    for (let pplr = plrm.GetNextPlayer(null); pplr !== null; pplr = plrm.GetNextPlayer(pplr)) {
      if (pplr.GetFlags() & kfPlrWinner) {
        sidmWinners = pplr.GetAllies();
        break;
      }
    }

    for (let pplr = plrm.GetNextPlayer(null); pplr !== null; pplr = plrm.GetNextPlayer(pplr)) {
      // Collect and send stats for each player; let the server sort it out.
      // The 116-byte WinStats struct (netmessage.h:355-367, pack=2), NATIVE
      // little-endian image first (the MD5/XOR run over native bytes).
      const ws = new Uint8Array(116);
      const dv = new DataView(ws.buffer);
      dv.setUint16(0, GetSideMask(pplr.GetSide()) & 0xffff, true); // sidm
      dv.setUint16(2, pplr.GetAllies() & 0xffff, true); // sidmAllies
      dv.setUint16(4, pplr.GetEnemyMobileUnitsKilled() & 0xffff, true);
      dv.setUint16(6, pplr.GetEnemyStructuresKilled() & 0xffff, true);
      dv.setUint16(8, pplr.GetMobileUnitsLost() & 0xffff, true);
      dv.setUint16(10, pplr.GetStructuresLost() & 0xffff, true);
      // ff at offset 12 — set below
      for (let i = 0; i < kutMax; i++) {
        // acut
        dv.setUint16(14 + i * 2, pplr.m_acut[i] & 0xffff, true);
      }
      for (let i = 0; i < kutMax; i++) {
        // acutBuilt
        dv.setUint16(60 + i * 2, pplr.m_acutBuilt[i] & 0xffff, true);
      }
      // (word dummy at 106 stays 0 — alignment)
      dv.setUint32(108, pplr.GetTotalCreditsAcquired() >>> 0, true); // cCreditsAcquired
      dv.setUint32(112, pplr.GetTotalCreditsConsumed() >>> 0, true); // cCreditsConsumed

      let ff = 0;
      // If there is a winner team, then the rest are losers
      if (sidmWinners !== 0) {
        if (GetSideMask(pplr.GetSide()) & sidmWinners) {
          ff |= kfwsWinner;
        } else {
          ff |= kfwsLoser;
        }
      } else {
        // This side isn't the winner. It doesn't know who the winners are,
        // but it does know it is a loser.
        if (pplr.GetFlags() & kfPlrLoser) {
          ff |= kfwsLoser;
        }
      }
      dv.setUint16(12, ff & 0xffff, true);

      // Pseudo hmac but with no secret! Minor hurdle for hackers.
      // MD5 over ws ‖ pid(le16) ‖ gameid(le32) ‖ localPid(le16)
      const mac = new Uint8Array(116 + 2 + 4 + 2);
      mac.set(ws, 0);
      const mdv = new DataView(mac.buffer);
      mdv.setUint16(116, pplr.m_pid & 0xffff, true);
      mdv.setUint32(118, this.m_gameid >>> 0, true);
      mdv.setUint16(122, (plrLocal?.m_pid ?? 0) & 0xffff, true);
      const hash = md5bytes(mac);

      // Another minor hurdle (Player.cpp:432-436): XOR-obfuscate the struct
      for (let i = 0; i < ws.length; i++) {
        ws[i] ^= hash[i & 0xf];
      }

      // The WIRE image (NetMessageByteOrderSwap → BE): pid, filler, hash,
      // then the 54 words + 2 dwords of the (obfuscated) WinStats — the exact
      // inverse of protocol/gamemsg.ts decodeWinStats.
      const inner = new Uint8Array(2 + 2 + 16 + 116);
      const idv = new DataView(inner.buffer);
      idv.setUint16(0, pplr.m_pid & 0xffff, false);
      idv.setUint16(2, 0, false); // filler
      inner.set(hash, 4);
      for (let i = 0; i < 54; i++) {
        idv.setUint16(20 + i * 2, dv.getUint16(i * 2, true), false);
      }
      idv.setUint32(128, dv.getUint32(108, true), false);
      idv.setUint32(132, dv.getUint32(112, true), false);

      this.mpTransport.SendNetMessage({
        nmid: NMID.CsWinStats,
        kind: 'rawinner',
        bytes: inner,
      });
    }
  }

  /** ~SimUIForm's MP teardown slice (SimUI.cpp:191-217): the lag/wait forms,
   *  the chatter signals + HideChat. (The transport callbacks are the
   *  embedder's to clear — it set them.) */
  Dispose(): void {
    this.m_pfrmLag?.Dispose();
    this.m_pfrmLag = null;
    this.m_domWaitingUnmount?.();
    this.m_domWaitingUnmount = null;
    if (this.m_pchatter !== null) {
      this.m_pchatter.disconnectPlayers(this.onPlayersButton_); // g_slothack.Disconnect
      this.m_pchatter.HideChat();
    }
  }
}
